import ClimateAutomationPanel from './ClimateAutomationPanel';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import './FarmDiary.css';
import { useNavigate } from 'react-router-dom';

/**
 * MemberSmartfarm.jsx
 *
 * 연결 API
 * GET  /api/member/smartfarm/sites
 * GET  /api/member/smartfarm/dashboard?site_id=1
 * GET  /api/member/smartfarm/states?site_id=1
 * POST /api/member/smartfarm/control
 * GET  /api/member/smartfarm/automation?site_id=1
 * POST /api/member/smartfarm/automation
 * GET  /api/member/smartfarm/request-status
 * POST /api/member/smartfarm/request
 * POST /api/member/smartfarm/request-cancel
 *
 * 필요 환경
 * - React
 * - React Router
 * - axios
 * - Bootstrap 5
 */
const MemberSmartfarm = () => {
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const realtimeSourceRef = useRef(null);
  const multiAutomationsRef = useRef([]);
  const automationWsRef = useRef(new Map());
  const nutrientsRef = useRef([]);

  const api = useMemo(
    () =>
      axios.create({
        withCredentials: true,
      }),
    []
  );

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [site, setSite] = useState(null);
  const [entities, setEntities] = useState([]);
  // MQTT Discovery 릴레이: HA 엔티티와 별도 관리
  const [mqttSwitches, setMqttSwitches] = useState([]);
  const [mqttBusyKey, setMqttBusyKey] = useState('');
  const [mqttError, setMqttError] = useState('');


  const [requestStatus, setRequestStatus] = useState(null);
  const [requestMemo, setRequestMemo] = useState('');

  const [pageLoading, setPageLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [controlLoadingId, setControlLoadingId] = useState('');

  // Mycodo 자동화 상태
  const [automationConfigured, setAutomationConfigured] = useState(null);
  const [automationActive, setAutomationActive] = useState(false);
  const [automationLoading, setAutomationLoading] = useState(false);

  // 자동관수 사용자 설정
  const [automationSettingsOpen, setAutomationSettingsOpen] = useState(false);
  const [automationSettingsLoading, setAutomationSettingsLoading] = useState(false);
  const [automationSettingsSaving, setAutomationSettingsSaving] = useState(false);
  const [automationSettings, setAutomationSettings] = useState({
    soil_threshold: 60,
    watering_seconds: 5,
    watering_interval: 180,
  });


  // 다중 자동관수
  const [multiAutomations, setMultiAutomations] = useState([]);
  const [automationEntities, setAutomationEntities] = useState({ soils: [], pumps: [] });
  const [multiAutomationLoading, setMultiAutomationLoading] = useState(false);
  const [multiAutomationSaving, setMultiAutomationSaving] = useState(false);
  const [multiAutomationActionId, setMultiAutomationActionId] = useState(null);
  const [multiPumpActionId, setMultiPumpActionId] = useState(null);
  const [collapsedAutomationIds, setCollapsedAutomationIds] = useState(() => null);
  const [editingAutomationId, setEditingAutomationId] = useState(null);
  const [multiAutomationForm, setMultiAutomationForm] = useState({
    name: '자동관수',
    soil_device_id: '',
    soil_entity_id: '',
    pump_device_id: '',
    pump_entity_id: '',
    soil_threshold: 60,
    watering_seconds: 5,
    watering_interval: 180,
  });

  // 양액기 (Mycodo Conditional 설정, 생성 시 비활성)
  const [nutrientOpen, setNutrientOpen] = useState(false);
  const [collapsedNutrientIds, setCollapsedNutrientIds] = useState(null);
  const [nutrientBusy, setNutrientBusy] = useState(false);
  const [nutrientRelayStates, setNutrientRelayStates] = useState({});
  const [nutrientRelayBusy, setNutrientRelayBusy] = useState('');

  const [nutrientBindings, setNutrientBindings] = useState({ inputs: [], outputs: [] });
  const [nutrients, setNutrients] = useState([]);
  const [nutrientDevices, setNutrientDevices] = useState({sensors:[],outputs:[]});
  const [nutrientAuto, setNutrientAuto] = useState({sensors:{ec:"",ph:""},outputs:{a:"",b:"",c:"",mix:"",water:"",supply:"",aux:""}});
  const [nutrientForm, setNutrientForm] = useState({
    name: '양액 자동화', ec_measurement: '', ph_measurement: '',
    target_ec: '1.5', target_ph: '6.0', dose_seconds: '1',
    mix_seconds: '60', settle_seconds: '60', cycle_interval: '300',
    outputs: { a: '', b: '', c: '', mix: '', water: '', supply: '', aux: '' },
  });
  const nutrientUpdate = (key, value) => setNutrientForm(prev => ({...prev, [key]:value}));
  const nutrientOutputUpdate = (key, value) => setNutrientForm(prev => ({
    ...prev, outputs:{...prev.outputs, [key]:value},
  }));
  const loadNutrient = useCallback(async (siteId) => {
    if (!siteId) return;
    // 장치 후보는 중앙 DB 기반이므로 현장 Mycodo가 꺼져 있어도 독립적으로 조회한다.
    const [devices, list, bindings] = await Promise.allSettled([
      api.get('/api/member/smartfarm/nutrient/devices', {params:{site_id:siteId}}),
      api.get('/api/member/smartfarm/nutrient', {params:{site_id:siteId}}),
      api.get('/api/member/smartfarm/nutrient/bindings', {params:{site_id:siteId}}),
    ]);
    if (devices.status === 'fulfilled' && devices.value.data?.ok) {
      setNutrientDevices({sensors:devices.value.data.sensors || [], outputs:devices.value.data.outputs || []});
    } else {
      setNutrientDevices({sensors:[],outputs:[]});
      setError(devices.status === 'rejected'
        ? (devices.reason?.response?.data?.error || '양액 장치 목록 조회 실패')
        : (devices.value?.data?.error || '양액 장치 목록 조회 실패'));
    }
    if (list.status === 'fulfilled' && list.value.data?.ok) {
      setNutrients(list.value.data.nutrients || []);
    }
    if (bindings.status === 'fulfilled' && bindings.value.data?.ok) {
      setNutrientBindings({inputs:bindings.value.data.inputs || [], outputs:bindings.value.data.outputs || []});
    }
  }, [api]);
  useEffect(() => { if (selectedSiteId) loadNutrient(selectedSiteId); }, [selectedSiteId, loadNutrient]);
  // Mycodo runtime status is read once; physical relay states arrive via ESP32 MQTT WebSocket.
  const refreshNutrientRelays = useCallback(async (items) => {
    const rows = Array.isArray(items) ? items : [];
    if (!rows.length) return;
    const results = await Promise.allSettled(rows.map(row =>
      api.get(`/api/member/smartfarm/nutrient/${row.id}/relays`)));
    setNutrientRelayStates(prev => {
      const next = {...prev};
      rows.forEach((row,i) => {
        const result = results[i];
        // A delayed REST response must never replace a newer MQTT WebSocket state.
        // REST is used for initial state/automation-active metadata only.
        if (result.status === 'fulfilled' && result.value.data?.ok) {
          const incoming = result.value.data;
          const prior = next[row.id] || {};
          const states = {...(incoming.states || {})};
          Object.entries(prior.states || {}).forEach(([role, value]) => {
            if (value === 'on' || value === 'off') states[role] = value;
          });
          next[row.id] = {...prior, ...incoming, states};
        } else if (!next[row.id]) {
          next[row.id] = {ok:false, states:{}};
        }
      });
      return next;
    });
  }, [api]);
  useEffect(() => {
    if (!selectedSiteId || !nutrients.length) return undefined;
    refreshNutrientRelays(nutrients);
    // No periodic polling: physical state updates are event-driven below.
    return undefined;
  }, [selectedSiteId, nutrients.map(row => row.id).sort().join(','), refreshNutrientRelays]);
  const controlNutrientRelay = async (item, role, state) => {
    if (nutrientRelayBusy || nutrientRelayStates[item.id]?.active !== false) return;
    const busyKey = `${item.id}:${role}`;
    setNutrientRelayBusy(busyKey);
    setError('');
    try {
      const response = await api.post(`/api/member/smartfarm/nutrient/${item.id}/relays/${role}`, {state});
      if (!response.data?.ok) throw new Error(response.data?.error || '릴레이 제어 실패');
      setNotice(state === 'on' ? '릴레이 켜기를 요청했습니다.' : '릴레이 정지를 요청했습니다.');
      // ON/OFF state is updated only from ESP32 MQTT WebSocket events.
    } catch (err) {
      setError(err?.response?.data?.error || err.message || '릴레이 제어 실패');
    } finally {setNutrientRelayBusy('');}
  };
  const saveNutrient = async () => {
    if (!selectedSiteId || nutrientBusy) return;
    setNutrientBusy(true);
    try {
      const selected = (value) => { const [device_id,entity_id] = String(value||'').split('::'); return device_id && entity_id ? {device_id:Number(device_id),entity_id} : null; };
      const auto_devices = {sensors:{},outputs:{}};
      for (const kind of ['ec','ph']) auto_devices.sensors[kind]=selected(nutrientAuto.sensors[kind]);
      for (const kind of ['a','b','c','mix','water','supply','aux']) {
        const v=selected(nutrientAuto.outputs[kind]);
        if(v) auto_devices.outputs[kind]=v;
      }
      const result = await api.post('/api/member/smartfarm/nutrient', {
        auto_devices,
        ...nutrientForm, site_id:Number(selectedSiteId),
        target_ec:Number(nutrientForm.target_ec), target_ph:Number(nutrientForm.target_ph),
        dose_seconds:Number(nutrientForm.dose_seconds), mix_seconds:Number(nutrientForm.mix_seconds),
        settle_seconds:Number(nutrientForm.settle_seconds), cycle_interval:Number(nutrientForm.cycle_interval),
      });
      if (!result.data?.ok) throw new Error(result.data?.error || '생성 실패');
      setNotice('Mycodo Input·Output·Function 자동 등록 요청이 완료되었습니다 (Function 비활성).');
      setNutrientOpen(false);
      await loadNutrient(selectedSiteId);
    } catch (err) { setError(err?.response?.data?.error || err.message || '양액기 생성 실패'); }
    finally { setNutrientBusy(false); }
  };
  const deleteNutrient = async (item) => {
    if (!item?.id || nutrientBusy) return;
    if (!window.confirm(`“${item.name || '양액 자동화'}”를 삭제하시겠습니까?\n실행 중이면 먼저 중지해야 합니다.`)) return;
    setNutrientBusy(true);
    setError('');
    try {
      const response = await api.delete(`/api/member/smartfarm/nutrient/${item.id}`);
      if (response.data?.ok === false) throw new Error(response.data.error || '양액 자동화 삭제 실패');
      setNutrients(prev => prev.filter(row => String(row.id) !== String(item.id)));
      setNotice(response.data?.message || '양액 자동화를 삭제했습니다.');
      await loadNutrient(selectedSiteId);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || '양액 자동화 삭제 실패');
    } finally { setNutrientBusy(false); }
  };

  const [nutrientEditId,setNutrientEditId] = useState(null);
  const [nutrientEditForm,setNutrientEditForm] = useState({});
  const editNutrient = item => {
    setNutrientEditId(item.id);
    setNutrientEditForm(Object.fromEntries(
      ['target_ec','target_ph','dose_seconds','mix_seconds','settle_seconds','cycle_interval']
      .map(k=>[k,String(item[k] ?? '')])));
  };
  const updateNutrient = async () => {
    setNutrientBusy(true);
    try {
      await api.put(`/api/member/smartfarm/nutrient/${nutrientEditId}`,
        Object.fromEntries(Object.entries(nutrientEditForm).map(([k,v])=>[k,Number(v)])));
      setNutrientEditId(null);
      await loadNutrient(selectedSiteId);
    } catch(err) { setError(err?.response?.data?.error || '양액기 수정 실패'); }
    finally { setNutrientBusy(false); }
  };
  // 자동관수와 동일한 제어 패턴: 요청 -> 응답 확인 -> 화면 상태 반영 -> 목록 재조회.
  // 중앙 목록 API가 일시적으로 이전 값을 돌려줘도 제어 직후 버튼을 되돌리지 않는다.
  const controlNutrient = async (item, active) => {
    if (!item?.id || nutrientBusy) return;
    setNutrientBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post(
        `/api/member/smartfarm/nutrient/${item.id}/control`,
        { active: Boolean(active) }
      );
      const data = response.data || {};
      if (!data.ok) throw new Error(data.error || data.message || '양액 자동화 제어 실패');
      // 명령 응답은 실행 상태의 증거가 아니다. Mycodo runtime GET으로 재확인한다.
      setNutrients(prev => prev.map(row => String(row.id) === String(item.id)
        ? { ...row, active: null, status_verified: false } : row));
      await loadNutrient(selectedSiteId);
      setNotice(data.message || '제어 요청 완료. Mycodo 실행 상태를 다시 조회했습니다.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message ||
        err?.response?.data?.detail || err.message || '양액 자동화 제어 실패');
    } finally { setNutrientBusy(false); }
  };

  const [connected, setConnected] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [entityValues, setEntityValues] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [cameraRefreshKey, setCameraRefreshKey] = useState(0);
  const [cameraFullscreenOpen, setCameraFullscreenOpen] = useState(false);
  const [ptzBusy, setPtzBusy] = useState('');
  const [ptzErrors, setPtzErrors] = useState({});
  const moveCameraPtz = async (camera, direction) => {
    if (!selectedSiteId || ptzBusy) return;
    setPtzBusy(camera.entity_id);
    setPtzErrors((prev) => ({ ...prev, [camera.entity_id]: '' }));
    try {
      const { data } = await api.post('/api/member/smartfarm/camera-ptz', {
        site_id: Number(selectedSiteId), entity_id: camera.entity_id, direction,
      }, { timeout: 11000 });
      if (!data?.ok) throw new Error(data?.error || 'PTZ 제어 실패');
    } catch (err) {
      setPtzErrors((prev) => ({ ...prev, [camera.entity_id]: err?.response?.data?.error || err.message || 'PTZ 제어 실패' }));
    } finally {
      setPtzBusy('');
    }
  };
  // PTZ 방향 버튼: 전체보기 버튼과 동일한 회색 테두리/검정 화살표 스타일.
  const renderCameraPtz = (camera) => camera.ptz_supported !== true ? null : (
    <div className="d-flex align-items-center gap-1" role="group" aria-label="카메라 PTZ 방향 제어">
      {[
        ['left', '◀', '왼쪽'],
        ['up', '▲', '위쪽'],
        ['down', '▼', '아래쪽'],
        ['right', '▶', '오른쪽'],
      ].map(([direction, arrow, label]) => (
        <button
          key={direction}
          type="button"
          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
          style={{ color: '#212529', backgroundColor: '#f1f3f5', minWidth: 30, height: 31, padding: '0 6px' }}
          disabled={Boolean(ptzBusy) || !camera.available}
          onClick={() => moveCameraPtz(camera, direction)}
          aria-label={`카메라 ${label} 이동`}
          title={`${label} 이동`}
        >
          <span aria-hidden="true" style={{ fontSize: '0.75rem', lineHeight: 1 }}>{arrow}</span>
        </button>
      ))}
      {ptzErrors[camera.entity_id] && (
        <span className="text-danger small" role="alert">{ptzErrors[camera.entity_id]}</span>
      )}
    </div>
  );

  const [registeredCameras, setRegisteredCameras] = useState([]);
  const [cameraStreamUrls, setCameraStreamUrls] = useState({});
  const [cameraStreamProviders, setCameraStreamProviders] = useState({});
  const [cameraStreamErrors, setCameraStreamErrors] = useState({});
  const [cameraImageReady, setCameraImageReady] = useState({});
  // CCTV 재연결만 관리한다. 자동화/상태 갱신 타이머와 무관하다.
  const cameraRetryTimersRef = useRef(new Map());
  const cameraRetryCountsRef = useRef(new Map());
  const cameraRefreshRequestRef = useRef(0);
  const cameraWatchdogTimersRef = useRef(new Map());

  const reconnectCamera = useCallback(() => {
    if (document.visibilityState === 'hidden') return;
    cameraRetryTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    cameraRetryTimersRef.current.clear();
    cameraRetryCountsRef.current.clear();
    cameraWatchdogTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    cameraWatchdogTimersRef.current.clear();
    setCameraImageReady({});
    cameraRefreshRequestRef.current += 1;
    setCameraRefreshKey(cameraRefreshRequestRef.current);
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectCamera();
    };
    const onOnline = () => reconnectCamera();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      cameraRetryTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      cameraRetryTimersRef.current.clear();
      cameraWatchdogTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      cameraWatchdogTimersRef.current.clear();
    };
  }, [reconnectCamera]);

  // 영상 요청이 열린 채 onLoad/onError 어느 쪽도 오지 않는 경우를 감지한다.
  // URL 발급 성공 후에만 감시를 시작하며 정상 재생 시 즉시 해제한다.
  const markCameraImageReady = useCallback((entityId) => {
    const watchdog = cameraWatchdogTimersRef.current.get(entityId);
    if (watchdog) window.clearTimeout(watchdog);
    cameraWatchdogTimersRef.current.delete(entityId);
    const retry = cameraRetryTimersRef.current.get(entityId);
    if (retry) window.clearTimeout(retry);
    cameraRetryTimersRef.current.delete(entityId);
    cameraRetryCountsRef.current.delete(entityId);
    setCameraImageReady((previous) => ({ ...previous, [entityId]: true }));
    setCameraStreamErrors((previous) => ({ ...previous, [entityId]: '' }));
  }, []);

  const onCameraImageError = useCallback((entityId) => {
    if (document.visibilityState === 'hidden' || !navigator.onLine) return;
    if (cameraRetryTimersRef.current.has(entityId)) return;
    const attempt = cameraRetryCountsRef.current.get(entityId) || 0;
    if (attempt >= 3) {
      setCameraStreamErrors((previous) => ({
        ...previous, [entityId]: '영상 연결 실패 · 영상 새로고침을 눌러주세요.',
      }));
      return;
    }
    setCameraImageReady((previous) => ({ ...previous, [entityId]: false }));
    cameraRetryCountsRef.current.set(entityId, attempt + 1);
    const timer = window.setTimeout(() => {
      cameraRetryTimersRef.current.delete(entityId);
      if (document.visibilityState === 'visible') {
        cameraRefreshRequestRef.current += 1;
        setCameraRefreshKey(cameraRefreshRequestRef.current);
      }
    }, Math.min(2000 * (attempt + 1), 6000));
    cameraRetryTimersRef.current.set(entityId, timer);
  }, []);

  const handleApiError = useCallback(
    (err, fallbackMessage) => {
      const status = err?.response?.status;
      const redirect = err?.response?.data?.redirect;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        fallbackMessage;

      if (status === 401) {
        navigate(redirect || '/login', { replace: true });
        return null;
      }

      setError(message);
      return message;
    },
    [navigate]
  );

  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  };

  const getStatusText = (status) => {
    const labels = {
      REQUEST: '신청 접수',
      SETTING: '설치 준비 중',
      COMPLETE: '설치 완료',
      CANCEL: '신청 취소',
      REJECT: '신청 반려',
    };

    return labels[status] || status || '신청 내역 없음';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'COMPLETE') return 'text-bg-success';
    if (status === 'SETTING') return 'text-bg-warning';
    if (status === 'REQUEST') return 'text-bg-primary';
    if (status === 'REJECT') return 'text-bg-danger';
    return 'text-bg-secondary';
  };

  const initializeEntityValues = useCallback((nextEntities) => {
    setEntityValues((prev) => {
      const next = { ...prev };

      nextEntities.forEach((entity) => {
        const attrs = entity.attributes || {};

        if (entity.domain === 'climate') {
          next[`${entity.entity_id}:temperature`] =
            attrs.temperature ??
            attrs.current_temperature ??
            24;

          next[`${entity.entity_id}:hvac_mode`] =
            attrs.hvac_mode ??
            (Array.isArray(attrs.hvac_modes)
              ? attrs.hvac_modes[0]
              : '');
        }

        if (entity.domain === 'number') {
          next[`${entity.entity_id}:value`] =
            entity.state ?? attrs.min ?? 0;
        }

        if (entity.domain === 'select') {
          next[`${entity.entity_id}:option`] =
            entity.state ??
            (Array.isArray(attrs.options)
              ? attrs.options[0]
              : '');
        }
      });

      return next;
    });
  }, []);

  const loadRequestStatus = useCallback(async () => {
    try {
      const response = await api.get(
        '/api/member/smartfarm/request-status'
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청 상태를 불러오지 못했습니다.'
        );
      }

      setRequestStatus(data);
    } catch (err) {
      handleApiError(
        err,
        '스마트팜 신청 상태를 불러오지 못했습니다.'
      );
    }
  }, [api, handleApiError]);

  const loadSites = useCallback(async () => {
    const response = await api.get('/api/member/smartfarm/sites');
    const data = response.data || {};

    if (!data.ok) {
      throw new Error(
        data.error || '스마트팜 목록을 불러오지 못했습니다.'
      );
    }

    const nextSites = Array.isArray(data.sites)
      ? data.sites
      : [];

    setSites(nextSites);

    setSelectedSiteId((prev) => {
      if (
        prev &&
        nextSites.some(
          (item) => String(item.id) === String(prev)
        )
      ) {
        return prev;
      }

      return nextSites[0]?.id
        ? String(nextSites[0].id)
        : '';
    });

    return nextSites;
  }, [api]);

  const loadDashboard = useCallback(
    async (siteId, options = {}) => {
      const { silent = false } = options;

      if (!siteId) {
        setSite(null);
        setEntities([]);
        setConnected(false);
        return;
      }

      if (!silent) {
        setDashboardLoading(true);
      }

      setError('');

      try {
        const response = await api.get(
          '/api/member/smartfarm/dashboard',
          {
            params: {
              site_id: Number(siteId),
            },
          }
        );

        const data = response.data || {};

        if (!data.ok) {
          throw new Error(
            data.error ||
              '스마트팜 대시보드를 불러오지 못했습니다.'
          );
        }

        if (!data.has_site) {
          setSite(null);
          setEntities([]);
          setConnected(false);
          return;
        }

        const nextEntities = Array.isArray(data.entities)
          ? data.entities
          : [];

        setSite(data.site || null);
        setEntities(nextEntities);
        setConnected(Boolean(data.connected));
        setLastRefreshedAt(new Date());
        initializeEntityValues(nextEntities);

        if (data.connection_error) {
          setError(data.connection_error);
        }
      } catch (err) {
        handleApiError(
          err,
          '스마트팜 대시보드를 불러오지 못했습니다.'
        );
      } finally {
        if (!silent) {
          setDashboardLoading(false);
        }
      }
    },
    [api, handleApiError, initializeEntityValues]
  );

  const loadAutomationStatus = useCallback(
    async (siteId) => {
      if (!siteId) {
        setAutomationConfigured(null);
        setAutomationActive(false);
        return;
      }

      try {
        const response = await api.get(
          '/api/member/smartfarm/automation',
          {
            params: {
              site_id: Number(siteId),
            },
          }
        );

        const data = response.data || {};

        if (!data.ok) {
          throw new Error(
            data.error || '자동화 상태를 불러오지 못했습니다.'
          );
        }

        setAutomationConfigured(Boolean(data.configured));
        setAutomationActive(Boolean(data.active));
      } catch (err) {
        setAutomationConfigured(null);
        setAutomationActive(false);
        handleApiError(
          err,
          '자동화 상태를 불러오지 못했습니다.'
        );
      }
    },
    [api, handleApiError]
  );

  const initializePage = useCallback(async () => {
    setPageLoading(true);
    setError('');

    try {
      const nextSites = await loadSites();
      await loadRequestStatus();

      if (nextSites.length > 0) {
        await loadDashboard(String(nextSites[0].id));
      }
    } catch (err) {
      handleApiError(
        err,
        '스마트팜 정보를 불러오지 못했습니다.'
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    handleApiError,
    loadDashboard,
    loadRequestStatus,
    loadSites,
  ]);

  useEffect(() => {
    initializePage();
  }, [initializePage]);

  useEffect(() => {
    if (!selectedSiteId || pageLoading) return;

    if (String(site?.id || '') === String(selectedSiteId)) return;

    loadDashboard(selectedSiteId);
  }, [loadDashboard, pageLoading, selectedSiteId, site?.id]);

  useEffect(() => {
    if (!selectedSiteId || pageLoading) {
      setAutomationConfigured(null);
      setAutomationActive(false);
      return;
    }

    loadAutomationStatus(selectedSiteId);
  }, [loadAutomationStatus, pageLoading, selectedSiteId]);

  const refreshStates = useCallback(
    async (silent = false) => {
      if (!selectedSiteId) return;

      if (!silent) {
        setRefreshing(true);
        setError('');
      }

      try {
        const response = await api.get(
          '/api/member/smartfarm/states',
          {
            params: {
              site_id: Number(selectedSiteId),
            },
          }
        );

        const data = response.data || {};

        if (!data.ok) {
          throw new Error(
            data.error || '기기 상태를 갱신하지 못했습니다.'
          );
        }

        const nextEntities = Array.isArray(data.entities)
          ? data.entities
          : [];

        setEntities((previous) => {
          const updates = new Map(nextEntities.map((entity) => [entity.entity_id, entity]));
          const merged = previous.map((entity) => {
            const update = updates.get(entity.entity_id);
            updates.delete(entity.entity_id);
            return update ? { ...entity, ...update } : entity;
          });
          return [...merged, ...updates.values()];
        });
        setConnected(Boolean(data.connected));
        setLastRefreshedAt(new Date());
        initializeEntityValues(nextEntities);
      } catch (err) {
        setConnected(false);

        if (!silent) {
          handleApiError(
            err,
            '기기 상태를 갱신하지 못했습니다.'
          );
        }
      } finally {
        if (!silent) {
          setRefreshing(false);
        }
      }
    },
    [
      api,
      handleApiError,
      initializeEntityValues,
      selectedSiteId,
    ]
  );

  useEffect(() => {
    if (refreshTimerRef.current) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (autoRefresh && selectedSiteId) {
      refreshTimerRef.current = window.setInterval(() => {
        refreshStates(true);
      }, 10000);
    }

    return () => {
      if (refreshTimerRef.current) {
        window.clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshStates, selectedSiteId]);

  useEffect(() => {
    if (realtimeSourceRef.current) {
      realtimeSourceRef.current.close();
      realtimeSourceRef.current = null;
    }

    if (!selectedSiteId || pageLoading || true) return undefined;

    const source = new EventSource(
      `/api/member/smartfarm/events?site_id=${encodeURIComponent(selectedSiteId)}`,
      { withCredentials: true }
    );

    realtimeSourceRef.current = source;

    const handleConnected = () => {
      setConnected(true);
    };

    const handleStateChanged = (event) => {
      try {
        const data = JSON.parse(event.data || '{}');
        const updatedEntity = data.entity;

        if (
          String(data.site_id) !== String(selectedSiteId) ||
          !updatedEntity?.entity_id
        ) {
          return;
        }

        setEntities((prev) =>
          prev.map((entity) =>
            entity.entity_id === updatedEntity.entity_id
              ? { ...entity, ...updatedEntity }
              : entity
          )
        );

        initializeEntityValues([updatedEntity]);
        setConnected(true);
        setLastRefreshedAt(new Date());
      } catch (eventError) {
        console.error('스마트팜 실시간 이벤트 처리 실패:', eventError);
      }
    };

    const handleRealtimeError = () => {
      // EventSource가 자동 재연결하므로 기존 10초 갱신은 그대로 유지한다.
      setConnected(false);
    };

    source.addEventListener('connected', handleConnected);
    source.addEventListener('state_changed', handleStateChanged);
    source.addEventListener('error', handleRealtimeError);

    return () => {
      source.removeEventListener('connected', handleConnected);
      source.removeEventListener('state_changed', handleStateChanged);
      source.removeEventListener('error', handleRealtimeError);
      source.close();

      if (realtimeSourceRef.current === source) {
        realtimeSourceRef.current = null;
      }
    };
  }, [initializeEntityValues, pageLoading, selectedSiteId]);

  useEffect(() => {
    if (!cameraFullscreenOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setCameraFullscreenOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [cameraFullscreenOpen]);

  const updateEntityState = (updatedEntity) => {
    if (!updatedEntity?.entity_id) return;

    setEntities((prev) =>
      prev.map((entity) =>
        entity.entity_id === updatedEntity.entity_id
          ? { ...entity, ...updatedEntity }
          : entity
      )
    );

    initializeEntityValues([updatedEntity]);
  };

  const controlEntity = async (
    entity,
    service,
    serviceData = {}
  ) => {
    if (
      !selectedSiteId ||
      !entity?.entity_id ||
      controlLoadingId
    ) {
      return;
    }

    setControlLoadingId(entity.entity_id);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/control',
        {
          site_id: Number(selectedSiteId),
          entity_id: entity.entity_id,
          service,
          service_data: serviceData,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '기기 제어에 실패했습니다.'
        );
      }

      setConnected(Boolean(data.connected));
      updateEntityState(data.entity);
      setLastRefreshedAt(new Date());
      setNotice(
        `${getDisplayName(entity)}: ${
          data.message || '제어가 완료되었습니다.'
        }`
      );
    } catch (err) {
      handleApiError(err, '기기 제어에 실패했습니다.');
    } finally {
      setControlLoadingId('');
    }
  };

  const loadAutomationSettings = async () => {
    if (!selectedSiteId || automationSettingsLoading) return;

    setAutomationSettingsLoading(true);
    setError('');

    try {
      const response = await api.get(
        '/api/member/smartfarm/automation-settings',
        { params: { site_id: Number(selectedSiteId) } }
      );
      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '자동관수 설정을 불러오지 못했습니다.');
      }

      setAutomationSettings({
        soil_threshold: Number(data.settings?.soil_threshold ?? 60),
        watering_seconds: Number(data.settings?.watering_seconds ?? 5),
        watering_interval: Number(data.settings?.watering_interval ?? 180),
      });
    } catch (err) {
      handleApiError(err, '자동관수 설정을 불러오지 못했습니다.');
    } finally {
      setAutomationSettingsLoading(false);
    }
  };

  const openAutomationSettings = async () => {
    setAutomationSettingsOpen(true);
    await loadAutomationSettings();
  };

  const saveAutomationSettings = async () => {
    if (!selectedSiteId || automationSettingsSaving) return;

    const soilThreshold = Number(automationSettings.soil_threshold);
    const wateringSeconds = Number(automationSettings.watering_seconds);
    const wateringInterval = Number(automationSettings.watering_interval);

    if (!Number.isFinite(soilThreshold) || soilThreshold < 1 || soilThreshold > 100) {
      setError('토양수분 기준은 1~100% 사이로 입력하세요.');
      return;
    }
    if (!Number.isInteger(wateringSeconds) || wateringSeconds < 1 || wateringSeconds > 3600) {
      setError('1회 관수시간은 1~3600초 사이의 정수로 입력하세요.');
      return;
    }
    if (!Number.isInteger(wateringInterval) || wateringInterval < 10 || wateringInterval > 86400) {
      setError('재관수 대기시간은 10~86400초 사이의 정수로 입력하세요.');
      return;
    }

    setAutomationSettingsSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/automation-settings',
        {
          site_id: Number(selectedSiteId),
          soil_threshold: soilThreshold,
          watering_seconds: wateringSeconds,
          watering_interval: wateringInterval,
        }
      );
      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '자동관수 설정 저장에 실패했습니다.');
      }

      setAutomationSettings({
        soil_threshold: Number(data.settings?.soil_threshold ?? soilThreshold),
        watering_seconds: Number(data.settings?.watering_seconds ?? wateringSeconds),
        watering_interval: Number(data.settings?.watering_interval ?? wateringInterval),
      });
      setAutomationSettingsOpen(false);
      setNotice(data.message || '자동관수 설정을 저장했습니다.');
    } catch (err) {
      handleApiError(err, '자동관수 설정 저장에 실패했습니다.');
    } finally {
      setAutomationSettingsSaving(false);
    }
  };

  const controlAutomation = async (activate) => {
    if (
      !selectedSiteId ||
      automationLoading ||
      (activate === false && automationConfigured !== true)
    ) {
      return;
    }

    setAutomationLoading(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/automation',
        {
          site_id: Number(selectedSiteId),
          activate: Boolean(activate),
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '자동화 제어에 실패했습니다.'
        );
      }

      setAutomationActive(Boolean(data.active));
      setNotice(
        data.message ||
          (activate
            ? '자동화가 시작되었습니다.'
            : '자동화가 중지되었습니다.')
      );

      await loadAutomationStatus(selectedSiteId);
    } catch (err) {
      handleApiError(err, '자동화 제어에 실패했습니다.');
    } finally {
      setAutomationLoading(false);
    }
  };

  const loadMultiAutomations = useCallback(async (siteId, options = {}) => {
    const { silent = false } = options;

    if (!siteId) {
      setMultiAutomations([]);
      setAutomationEntities({ soils: [], pumps: [] });
      return;
    }

    if (!silent) setMultiAutomationLoading(true);

    try {
      const [listResponse, entityResponse] = await Promise.all([
        api.get('/api/member/smartfarm/automations', {
          params: { site_id: Number(siteId) },
        }),
        api.get('/api/member/smartfarm/automation-entities', {
          params: { site_id: Number(siteId) },
        }),
      ]);

      const listData = listResponse.data || {};
      const entityData = entityResponse.data || {};

      if (!listData.ok) {
        throw new Error(listData.error || '자동관수 목록을 불러오지 못했습니다.');
      }
      if (!entityData.ok) {
        throw new Error(entityData.error || '자동관수 기기 목록을 불러오지 못했습니다.');
      }

      setMultiAutomations(Array.isArray(listData.automations) ? listData.automations : []);
      setAutomationEntities({
        soils: Array.isArray(entityData.soils) ? entityData.soils : [],
        pumps: Array.isArray(entityData.pumps) ? entityData.pumps : [],
      });
    } catch (err) {
      handleApiError(err, '자동관수 정보를 불러오지 못했습니다.');
    } finally {
      if (!silent) setMultiAutomationLoading(false);
    }
  }, [api, handleApiError]);

  useEffect(() => {
    if (!selectedSiteId || pageLoading) {
      setMultiAutomations([]);
      setAutomationEntities({ soils: [], pumps: [] });
      return;
    }

    loadMultiAutomations(selectedSiteId);
  }, [loadMultiAutomations, pageLoading, selectedSiteId]);


  // ----------------------------------------------------------
  // 자동관수 + 양액기 MQTT 실제 상태 -> 기존 ESP32 WebSocket 실시간 반영
  //
  // 제어 경로는 기존 Mycodo Output을 그대로 사용한다.
  // 화면의 토양수분/릴레이 상태만 ESP32가 MQTT로 발행한 실제 상태를
  // site_id + device_id + entity 기준으로 받아 갱신한다.
  // ----------------------------------------------------------
  useEffect(() => {
    multiAutomationsRef.current = multiAutomations;
  }, [multiAutomations]);

  useEffect(() => { nutrientsRef.current = nutrients; }, [nutrients]);

  useEffect(() => {
    const sockets = automationWsRef.current;
    const reconnectTimers = new Map();
    let disposed = false;

    const closeAll = () => {
      reconnectTimers.forEach((timerId) => window.clearTimeout(timerId));
      reconnectTimers.clear();

      sockets.forEach((socket) => {
        try {
          socket.onopen = null;
          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          socket.close();
        } catch (_) {
          // 이미 닫힌 소켓은 무시한다.
        }
      });
      sockets.clear();
    };

    if (!selectedSiteId || pageLoading || (multiAutomations.length === 0 && nutrientsRef.current.length === 0)) {
      closeAll();
      return closeAll;
    }

    const normalizeId = (value) => String(value ?? '').trim().toLowerCase();
    const normalizeName = (value) =>
      String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

    const findEntity = (entitiesList, entityId, entityName, domain) => {
      const targetId = normalizeId(entityId);
      const targetName = normalizeName(entityName);

      return (entitiesList || []).find((entity) => {
        if (domain && String(entity?.domain || '').toLowerCase() !== domain &&
            !String(entity?.entity_id || '').toLowerCase().startsWith(`${domain}.`)) return false;

        const mqttId = normalizeId(entity?.entity_id);
        const objectId = normalizeId(entity?.object_id);
        const uniqueId = normalizeId(entity?.unique_id);

        if (targetId && (mqttId === targetId || objectId === targetId || uniqueId === targetId)) {
          return true;
        }

        // Builder의 ESPHome id와 MQTT Discovery key 표현이 다른 장치용 보조 매칭.
        // device_id가 먼저 일치한 장치 내부에서만 이름을 비교하므로
        // 다른 ESP32의 동명 센서/릴레이와 섞이지 않는다.
        const mqttName = normalizeName(entity?.name || entity?.friendly_name);
        return Boolean(targetName && mqttName === targetName);
      });
    };

    const applyDeviceState = (payload) => {
      if (disposed || payload?.type !== 'state') return;
      if (Number(payload.site_id) !== Number(selectedSiteId)) return;

      const deviceId = Number(payload.device_id);
      const mqttEntities = Array.isArray(payload.entities) ? payload.entities : [];
      if (!Number.isInteger(deviceId) || deviceId <= 0) return;

      setMultiAutomations((prev) =>
        prev.map((automation) => {
          let next = automation;

          if (Number(automation.soil_device_id) === deviceId) {
            const soil = findEntity(
              mqttEntities,
              automation.soil_entity_id,
              automation.soil_entity_name,
              'sensor'
            );
            const soilValue = Number(soil?.value);

            if (soil && Number.isFinite(soilValue)) {
              next = { ...next, soil_value: soilValue };
            }
          }

          if (Number(automation.pump_device_id) === deviceId) {
            const exactPump = findEntity(
              mqttEntities,
              automation.pump_entity_id,
              automation.pump_entity_name,
              'switch'
            );
            // A renamed display label is not the ESPHome/MQTT control identity.
            // Only fall back when this device reports exactly one switch.
            const deviceSwitches = mqttEntities.filter(entity =>
              String(entity?.domain || '').toLowerCase() === 'switch' ||
              String(entity?.entity_id || '').toLowerCase().startsWith('switch.')
            );
            const pump = exactPump || (deviceSwitches.length === 1 ? deviceSwitches[0] : null);

            if (pump) {
              const raw = pump.value;
              const normalized = String(raw ?? '').trim().toLowerCase();
              const isOn =
                raw === true ||
                normalized === 'on' ||
                normalized === 'true' ||
                normalized === '1';
              const isOff =
                raw === false ||
                normalized === 'off' ||
                normalized === 'false' ||
                normalized === '0';

              if (isOn || isOff) {
                next = { ...next, pump_state: isOn ? 'on' : 'off' };
              }
            }
          }

          return next;
        })
      );

      // 양액 릴레이도 동일한 MQTT state 이벤트에서만 실제 ON/OFF를 판정한다.
      // 같은 이름의 다른 ESP32를 혼동하지 않도록 device_id로 먼저 구분한다.
      setNutrientRelayStates((prev) => {
        let changed = false;
        const next = {...prev};
        nutrientsRef.current.forEach((item) => {
          const outputs = item.config?.auto_devices?.outputs || {};
          const states = {...(next[item.id]?.states || {})};
          let matched = false;
          Object.entries(outputs).forEach(([role, binding]) => {
            if (Number(binding?.device_id) !== deviceId) return;
            const entity = findEntity(mqttEntities, binding.entity_id, binding.entity_name, 'switch');
            if (!entity) return;
            const raw = String(entity.value ?? '').trim().toLowerCase();
            const value = ['on','true','1'].includes(raw) ? 'on' :
              ['off','false','0'].includes(raw) ? 'off' : null;
            if (value && states[role] !== value) { states[role] = value; matched = true; }
          });
          if (matched) {
            next[item.id] = {...next[item.id], states, ok:true};
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };

    const deviceIds = Array.from(
      new Set(
        [
          ...multiAutomations.flatMap((item) => [Number(item.soil_device_id), Number(item.pump_device_id)]),
          ...nutrientsRef.current.flatMap((item) =>
            Object.values(item.config?.auto_devices?.outputs || {}).map((binding) => Number(binding?.device_id))
          ),
        ]
          .filter((id) => Number.isInteger(id) && id > 0)
      )
    ).sort((a, b) => a - b);

    const wanted = new Set(deviceIds);

    // 설정 변경/삭제로 더 이상 필요 없는 장치 연결은 즉시 닫는다.
    sockets.forEach((socket, deviceId) => {
      if (!wanted.has(Number(deviceId))) {
        try {
          socket.onclose = null;
          socket.close();
        } catch (_) {
          // noop
        }
        sockets.delete(deviceId);
      }
    });

    const connectDevice = (deviceId) => {
      if (disposed || !wanted.has(deviceId)) return;

      const existing = sockets.get(deviceId);
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url =
        `${scheme}://${window.location.host}/api/member/smartfarm/ws` +
        `?site_id=${encodeURIComponent(selectedSiteId)}` +
        `&device_id=${encodeURIComponent(deviceId)}`;

      const socket = new WebSocket(url);
      sockets.set(deviceId, socket);

      socket.onmessage = (event) => {
        try {
          applyDeviceState(JSON.parse(event.data || '{}'));
        } catch (err) {
          console.warn('자동관수 MQTT WebSocket 메시지 처리 실패:', err);
        }
      };

      socket.onerror = () => {
        // onclose에서 재연결한다. 기존 화면값은 유지한다.
      };

      socket.onclose = () => {
        if (sockets.get(deviceId) === socket) {
          sockets.delete(deviceId);
        }
        if (disposed || !wanted.has(deviceId)) return;

        const oldTimer = reconnectTimers.get(deviceId);
        if (oldTimer) window.clearTimeout(oldTimer);

        const timerId = window.setTimeout(() => {
          reconnectTimers.delete(deviceId);
          connectDevice(deviceId);
        }, 2000);
        reconnectTimers.set(deviceId, timerId);
      };
    };

    deviceIds.forEach(connectDevice);

    return () => {
      disposed = true;
      closeAll();
    };
  }, [
    pageLoading,
    selectedSiteId,
    multiAutomations
      .map((item) => `${item.soil_device_id}:${item.pump_device_id}`)
      .sort()
      .join('|'),
    nutrients.map((item) => `${item.id}:${Object.values(item.config?.auto_devices?.outputs || {}).map(x => x?.device_id).join(',')}`).sort().join('|'),
  ]);

  const toggleAutomationCollapsed = (automationId) => {
    setCollapsedAutomationIds((prev) => {
      const next = new Set(prev || multiAutomations.map((item) => item.id));
      if (next.has(automationId)) {
        next.delete(automationId);
      } else {
        next.add(automationId);
      }
      return next;
    });
  };

  const resetMultiAutomationForm = () => {
    setEditingAutomationId(null);
    setMultiAutomationForm({
      name: `자동관수 ${multiAutomations.length + 1}`,
      soil_device_id: '',
      soil_entity_id: '',
      pump_device_id: '',
      pump_entity_id: '',
      soil_threshold: 60,
      watering_seconds: 5,
      watering_interval: 180,
    });
  };

  const openCreateAutomation = async () => {
    if (!selectedSiteId) return;
    await loadMultiAutomations(selectedSiteId);
    resetMultiAutomationForm();
    setAutomationSettingsOpen(true);
  };

  const openEditAutomation = (automation) => {
    setEditingAutomationId(automation.id);
    setMultiAutomationForm({
      name: automation.name || '자동관수',
      soil_device_id: automation.soil_device_id,
      soil_entity_id: automation.soil_entity_id,
      pump_device_id: automation.pump_device_id,
      pump_entity_id: automation.pump_entity_id,
      soil_threshold: Number(automation.soil_threshold ?? 60),
      watering_seconds: Number(automation.watering_seconds ?? 5),
      watering_interval: Number(automation.watering_interval ?? 180),
    });
    setAutomationSettingsOpen(true);
  };

  const saveMultiAutomation = async () => {
    if (!selectedSiteId || multiAutomationSaving) return;

    const soilThreshold = Number(multiAutomationForm.soil_threshold);
    const wateringSeconds = Number(multiAutomationForm.watering_seconds);
    const wateringInterval = Number(multiAutomationForm.watering_interval);

    if (!Number.isFinite(soilThreshold) || soilThreshold < 1 || soilThreshold > 100) {
      setError('토양수분 기준은 1~100% 사이로 입력하세요.');
      return;
    }
    if (!Number.isInteger(wateringSeconds) || wateringSeconds < 1 || wateringSeconds > 3600) {
      setError('1회 관수시간은 1~3600초 사이의 정수로 입력하세요.');
      return;
    }
    if (!Number.isInteger(wateringInterval) || wateringInterval < 10 || wateringInterval > 86400) {
      setError('재관수 대기시간은 10~86400초 사이의 정수로 입력하세요.');
      return;
    }

    if (!editingAutomationId && (!multiAutomationForm.soil_entity_id || !multiAutomationForm.pump_entity_id)) {
      setError('토양수분 센서와 관수 릴레이를 모두 선택하세요.');
      return;
    }

    setMultiAutomationSaving(true);
    setError('');
    setNotice('');

    try {
      const common = {
        name: String(multiAutomationForm.name || '자동관수').trim() || '자동관수',
        soil_threshold: soilThreshold,
        watering_seconds: wateringSeconds,
        watering_interval: wateringInterval,
      };

      let response;
      if (editingAutomationId) {
        response = await api.put(
          `/api/member/smartfarm/automations/${editingAutomationId}`,
          common
        );
      } else {
        response = await api.post('/api/member/smartfarm/automations', {
          site_id: Number(selectedSiteId),
          ...common,
          soil_device_id: Number(multiAutomationForm.soil_device_id),
          soil_entity_id: multiAutomationForm.soil_entity_id,
          pump_device_id: Number(multiAutomationForm.pump_device_id),
          pump_entity_id: multiAutomationForm.pump_entity_id,
        });
      }

      const data = response.data || {};
      if (!data.ok) throw new Error(data.error || '자동관수 저장에 실패했습니다.');

      setAutomationSettingsOpen(false);
      setNotice(data.message || '자동관수를 저장했습니다.');
      await loadMultiAutomations(selectedSiteId);
    } catch (err) {
      handleApiError(err, '자동관수 저장에 실패했습니다.');
    } finally {
      setMultiAutomationSaving(false);
    }
  };

  const controlMultiAutomation = async (automation) => {
    if (!automation?.id || multiAutomationActionId) return;

    setMultiAutomationActionId(automation.id);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        `/api/member/smartfarm/automations/${automation.id}/control`,
        { activate: !automation.active }
      );
      const data = response.data || {};
      if (!data.ok) throw new Error(data.error || '자동관수 제어에 실패했습니다.');

      setNotice(data.message || '자동관수 상태를 변경했습니다.');
      await loadMultiAutomations(selectedSiteId, { silent: true });
    } catch (err) {
      handleApiError(err, '자동관수 제어에 실패했습니다.');
    } finally {
      setMultiAutomationActionId(null);
    }
  };


  const controlMultiPump = async (automation, state) => {
    if (!automation?.id || multiPumpActionId) return;

    setMultiPumpActionId(automation.id);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        `/api/member/smartfarm/automations/${automation.id}/pump`,
        { state }
      );
      const data = response.data || {};
      if (!data.ok) throw new Error(data.error || '물펌프 제어에 실패했습니다.');

      // 펌프 제어 성공 시 해당 Mycodo 릴레이 카드의 상태만 즉시 반영한다.
      // 자동관수 실행/설정/삭제의 busy 상태와 분리하고,
      // 직후 목록 재조회가 이전 pump_state로 화면을 덮어쓰지 않도록 한다.
      setMultiAutomations((prev) =>
        prev.map((item) =>
          item.id === automation.id
            ? { ...item, pump_state: state }
            : item
        )
      );

      setNotice(data.message || (state === 'on' ? '물펌프를 켰습니다.' : '물펌프를 껐습니다.'));
    } catch (err) {
      handleApiError(err, '물펌프 제어에 실패했습니다.');
    } finally {
      setMultiPumpActionId(null);
    }
  };

  const deleteMultiAutomation = async (automation) => {
    if (!automation?.id || multiAutomationActionId) return;
    if (!window.confirm(`“${automation.name || '자동관수'}”를 삭제하시겠습니까?\n실행 중이면 먼저 중지됩니다.`)) return;

    setMultiAutomationActionId(automation.id);
    setError('');
    setNotice('');

    try {
      const response = await api.delete(`/api/member/smartfarm/automations/${automation.id}`);
      const data = response.data || {};
      if (!data.ok) throw new Error(data.error || '자동관수 삭제에 실패했습니다.');

      setNotice(data.message || '자동관수를 삭제했습니다.');
      setAutomationSettingsOpen(false);
      await loadMultiAutomations(selectedSiteId);
    } catch (err) {
      handleApiError(err, '자동관수 삭제에 실패했습니다.');
    } finally {
      setMultiAutomationActionId(null);
    }
  };

  const submitRequest = async (event) => {
    event.preventDefault();

    setRequestLoading(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/request',
        {
          memo: requestMemo.trim(),
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청에 실패했습니다.'
        );
      }

      setRequestMemo('');
      setNotice(
        data.message || '스마트팜 신청이 접수되었습니다.'
      );
      await loadRequestStatus();
    } catch (err) {
      handleApiError(err, '스마트팜 신청에 실패했습니다.');
    } finally {
      setRequestLoading(false);
    }
  };

  const cancelRequest = async () => {
    const requestId = requestStatus?.request?.id;

    if (!requestId || requestLoading) return;

    if (
      !window.confirm(
        '접수된 스마트팜 신청을 취소하시겠습니까?'
      )
    ) {
      return;
    }

    setRequestLoading(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/request-cancel',
        {
          request_id: requestId,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청 취소에 실패했습니다.'
        );
      }

      setNotice(
        data.message || '스마트팜 신청이 취소되었습니다.'
      );
      await loadRequestStatus();
    } catch (err) {
      handleApiError(
        err,
        '스마트팜 신청 취소에 실패했습니다.'
      );
    } finally {
      setRequestLoading(false);
    }
  };

  const getEntityIcon = (entity) => {
    const type =
      entity.device_class ||
      entity.entity_type ||
      entity.domain;

    const icons = {
      temperature: '🌡️',
      humidity: '💧',
      moisture: '🌱',
      illuminance: '☀️',
      motion: '🚶',
      door: '🚪',
      window: '🪟',
      connectivity: '📶',
      battery: '🔋',
      power: '⚡',
      pressure: '🧭',
      switch: '🔌',
      light: '💡',
      fan: '🌀',
      climate: '🌬️',
      cover: '🪟',
      camera: '📷',
      automation: '⚙️',
      button: '⏺️',
      number: '🔢',
      select: '🎛️',
      input_boolean: '🔘',
      sensor: '📊',
      binary_sensor: '◉',
    };

    return icons[type] || icons[entity.domain] || '📟';
  };

  const isOnState = (state) =>
    ['on', 'open', 'opening', 'heat', 'cool'].includes(
      String(state || '').toLowerCase()
    );

    const getDisplayName = (entity) => {
      const customName = String(entity.entity_name || '').trim();
    
      // 관리자가 등록한 표시이름을 최우선으로 사용
      if (customName) {
        return customName;
      }
    
      const type = String(
        entity.device_class || entity.entity_type || ''
      ).toLowerCase();
    
      const entityId = String(
        entity.entity_id || ''
      ).toLowerCase();
    
      const unit = String(
        entity.unit_of_measurement ||
          entity.attributes?.unit_of_measurement ||
          ''
      ).toLowerCase();
    
      const source = `${type} ${entityId} ${unit}`;
    
      if (
        type === 'temperature' ||
        source.includes('temperature') ||
        source.includes('온도') ||
        ['°c', '℃', 'celsius'].includes(unit)
      ) {
        return '온도';
      }
    
      if (
        type === 'humidity' ||
        source.includes('humidity') ||
        source.includes('습도') ||
        unit === '%rh'
      ) {
        return '습도';
      }
    
      if (
        type === 'moisture' ||
        source.includes('soil_moisture') ||
        source.includes('soil moisture') ||
        source.includes('토양 수분') ||
        source.includes('토양습도')
      ) {
        return '토양 수분';
      }
    
      if (
        type === 'illuminance' ||
        source.includes('illuminance') ||
        source.includes('조도') ||
        unit === 'lx' ||
        unit === 'lux'
      ) {
        return '조도';
      }
    
      const labels = {
        pressure: '기압',
        battery: '배터리',
        power: '전력',
        energy: '전력량',
        motion: '움직임',
        door: '문',
        window: '창문',
        connectivity: '연결 상태',
        switch: '스위치',
        light: '조명',
        fan: '환풍기',
        climate: '냉난방기',
        camera: '카메라',
      };
    
      if (labels[type]) {
        return labels[type];
      }
    
      if (labels[entity.domain]) {
        return labels[entity.domain];
      }
    
      if (entity.domain === 'sensor') {
        return '센서';
      }
    
      if (entity.domain === 'binary_sensor') {
        return '상태 센서';
      }
    
      return '기기';
    };

  const formatSensorValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '상태 없음';
    }

    const raw = String(value).trim();
    const numericMatch = raw.match(/-?\d+(?:\.\d+)?/);

    if (numericMatch) {
      const numericValue = Number(numericMatch[0]);
      if (Number.isFinite(numericValue)) {
        return numericValue.toLocaleString('ko-KR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
      }
    }

    return raw;
  };

  const getStateText = (entity) => {
    const state = entity.state;

    if (
      state === null ||
      state === undefined ||
      state === ''
    ) {
      return '상태 없음';
    }

    const translated = {
      on: '켜짐',
      off: '꺼짐',
      open: '열림',
      closed: '닫힘',
      opening: '여는 중',
      closing: '닫는 중',
      unavailable: '연결 안 됨',
      unknown: '알 수 없음',
      idle: '대기',
      heat: '난방',
      cool: '냉방',
      auto: '자동',
      dry: '제습',
      fan_only: '송풍',
      home: '정상',
      not_home: '외출',
    };

    const normalizedState = String(state).toLowerCase();
    if (translated[normalizedState]) {
      return translated[normalizedState];
    }

    return formatSensorValue(state);
  };

  const renderBasicPowerControl = (entity) => {
    const busy = controlLoadingId === entity.entity_id;
    const isOn = isOnState(entity.state);
  
    return (
      <div className="d-grid">
        {isOn ? (
          <button
            type="button"
            className="btn btn-secondary w-100"
            onClick={() => controlEntity(entity, 'turn_off')}
            disabled={busy || !entity.available}
          >
            끄기
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={() => controlEntity(entity, 'turn_on')}
            disabled={busy || !entity.available}
          >
            켜기
          </button>
        )}
      </div>
    );
  };

  const renderEntityControl = (entity) => {
    if (entity.controllable === false) return null;

    const busy = controlLoadingId === entity.entity_id;
    const attrs = entity.attributes || {};

    if (
      ['switch', 'light', 'fan', 'input_boolean'].includes(
        entity.domain
      )
    ) {
      return renderBasicPowerControl(entity);
    }

    if (entity.domain === 'automation') {
      return (
        <div className="d-grid gap-2 d-sm-flex">
          <button
            type="button"
            className="btn btn-primary flex-fill"
            onClick={() =>
              controlEntity(entity, 'trigger')
            }
            disabled={busy || !entity.available}
          >
            {busy ? '실행 중...' : '자동화 실행'}
          </button>

          {renderBasicPowerControl(entity)}
        </div>
      );
    }

    if (entity.domain === 'button') {
      return (
        <button
          type="button"
          className="btn btn-primary w-100"
          onClick={() =>
            controlEntity(entity, 'press')
          }
          disabled={busy || !entity.available}
        >
          {busy ? '실행 중...' : '버튼 실행'}
        </button>
      );
    }

    if (entity.domain === 'cover') {
      return (
        <div className="row g-2">
          <div className="col-4">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              onClick={() =>
                controlEntity(entity, 'open_cover')
              }
              disabled={busy || !entity.available}
            >
              열기
            </button>
          </div>

          <div className="col-4">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              onClick={() =>
                controlEntity(entity, 'stop_cover')
              }
              disabled={busy || !entity.available}
            >
              정지
            </button>
          </div>

          <div className="col-4">
            <button
              type="button"
              className="btn btn-outline-dark w-100"
              onClick={() =>
                controlEntity(entity, 'close_cover')
              }
              disabled={busy || !entity.available}
            >
              닫기
            </button>
          </div>
        </div>
      );
    }

    if (entity.domain === 'climate') {
      const tempKey = `${entity.entity_id}:temperature`;
      const modeKey = `${entity.entity_id}:hvac_mode`;
      const modes = Array.isArray(attrs.hvac_modes)
        ? attrs.hvac_modes
        : [];

      return (
        <div className="d-grid gap-3">
          <div className="input-group">
            <span className="input-group-text">
              목표 온도
            </span>
            <input
              type="number"
              className="form-control"
              step="0.5"
              value={entityValues[tempKey] ?? ''}
              onChange={(event) =>
                setEntityValues((prev) => ({
                  ...prev,
                  [tempKey]: event.target.value,
                }))
              }
              disabled={busy || !entity.available}
            />
            <span className="input-group-text">℃</span>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() =>
                controlEntity(
                  entity,
                  'set_temperature',
                  {
                    temperature: Number(
                      entityValues[tempKey]
                    ),
                  }
                )
              }
              disabled={
                busy ||
                !entity.available ||
                entityValues[tempKey] === ''
              }
            >
              설정
            </button>
          </div>

          {modes.length > 0 && (
            <div className="input-group">
              <span className="input-group-text">
                운전 모드
              </span>
              <select
                className="form-select"
                value={entityValues[modeKey] ?? ''}
                onChange={(event) =>
                  setEntityValues((prev) => ({
                    ...prev,
                    [modeKey]: event.target.value,
                  }))
                }
                disabled={busy || !entity.available}
              >
                {modes.map((mode) => (
                  <option key={mode} value={mode}>
                    {getStateText({ state: mode })}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() =>
                  controlEntity(
                    entity,
                    'set_hvac_mode',
                    {
                      hvac_mode:
                        entityValues[modeKey],
                    }
                  )
                }
                disabled={
                  busy ||
                  !entity.available ||
                  !entityValues[modeKey]
                }
              >
                적용
              </button>
            </div>
          )}

          {renderBasicPowerControl(entity)}
        </div>
      );
    }

    if (entity.domain === 'number') {
      const key = `${entity.entity_id}:value`;

      return (
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            min={attrs.min}
            max={attrs.max}
            step={attrs.step || 1}
            value={entityValues[key] ?? ''}
            onChange={(event) =>
              setEntityValues((prev) => ({
                ...prev,
                [key]: event.target.value,
              }))
            }
            disabled={busy || !entity.available}
          />

          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              controlEntity(entity, 'set_value', {
                value: Number(entityValues[key]),
              })
            }
            disabled={
              busy ||
              !entity.available ||
              entityValues[key] === ''
            }
          >
            값 설정
          </button>
        </div>
      );
    }

    if (entity.domain === 'select') {
      const key = `${entity.entity_id}:option`;
      const options = Array.isArray(attrs.options)
        ? attrs.options
        : [];

      return (
        <div className="input-group">
          <select
            className="form-select"
            value={entityValues[key] ?? ''}
            onChange={(event) =>
              setEntityValues((prev) => ({
                ...prev,
                [key]: event.target.value,
              }))
            }
            disabled={busy || !entity.available}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              controlEntity(entity, 'select_option', {
                option: entityValues[key],
              })
            }
            disabled={
              busy ||
              !entity.available ||
              !entityValues[key]
            }
          >
            적용
          </button>
        </div>
      );
    }

    return null;
  };

  const groupedEntities = useMemo(() => {
    const groups = {
      environment: [],
      control: [],
      camera: [],
      security: [],
      other: [],
    };

    entities.forEach((rawEntity) => {
      // 상태 응답에 domain이 빠져도 HA entity_id로 장치 종류를 복원한다.
      const entity = {
        ...rawEntity,
        domain: String(rawEntity.entity_id || '').split('.')[0] || rawEntity.domain,
      };
      const type =
        entity.device_class ||
        entity.entity_type ||
        '';

      if (
        [
          'switch',
          'light',
          'fan',
          'climate',
          'cover',
          'input_boolean',
          'automation',
          'button',
          'number',
          'select',
        ].includes(entity.domain)
      ) {
        groups.control.push(entity);
        return;
      }

      if (
        entity.domain === 'sensor' ||
        [
          'temperature',
          'humidity',
          'moisture',
          'illuminance',
          'pressure',
          'battery',
          'power',
        ].includes(type)
      ) {
        groups.environment.push(entity);
        return;
      }

      if (entity.domain === 'camera') {
        // HA camera entities remain intact; only CCTV registrations appear here.
        return;
      }

      if (['motion', 'door', 'window'].includes(type)) {
        groups.security.push(entity);
        return;
      }

      groups.other.push(entity);
    });

    groups.camera = registeredCameras;
    return groups;
  }, [entities, registeredCameras]);

  // CCTV registration is separate from Home Assistant entities.
  useEffect(() => {
    if (!selectedSiteId) { setRegisteredCameras([]); return undefined; }
    let cancelled = false;
    setRegisteredCameras([]);
    api.get('/api/member/smartfarm/cameras', {
      params: { site_id: Number(selectedSiteId) },
    }).then(({ data }) => {
      if (cancelled) return;
      if (!data?.ok) throw new Error(data?.error || 'CCTV 목록 조회 실패');
      setRegisteredCameras(Array.isArray(data.cameras) ? data.cameras : []);
    }).catch((err) => {
      if (!cancelled) setError(err?.response?.data?.error || err.message || 'CCTV 목록 조회 실패');
    });
    return () => { cancelled = true; };
  }, [api, selectedSiteId]);

  // 카메라 목록의 객체/배열 참조가 HA 상태 갱신 때마다 바뀌어도
  // 실제 카메라 구성이 같으면 signed URL을 다시 발급하지 않는다.
  const cameraEntityIdsKey = useMemo(
    () =>
      (groupedEntities.camera || [])
        .map((camera) => String(camera.entity_id || '').trim())
        .filter(Boolean)
        .sort()
        .join('|'),
    [groupedEntities.camera]
  );

  // AWS는 카메라 영상 본문을 중계하지 않고 signed URL만 발급한다.
  // 실제 영상은 현장 Home Assistant 도메인에서 브라우저로 직접 수신한다.
  useEffect(() => {
    const cameraEntityIds = cameraEntityIdsKey
      ? cameraEntityIdsKey.split('|').filter(Boolean)
      : [];

    if (!selectedSiteId || cameraEntityIds.length === 0) {
      setCameraStreamUrls({});
      setCameraStreamProviders({});
      setCameraStreamErrors({});
      setCameraImageReady({});
      return undefined;
    }

    let cancelled = false;

    const loadDirectCameraUrls = async () => {
      const results = await Promise.allSettled(
        cameraEntityIds.map(async (entityId) => {
          const response = await api.get(
            '/api/member/smartfarm/camera-url',
            {
              params: {
                site_id: Number(selectedSiteId),
                entity_id: entityId,
                _ts: cameraRefreshKey || undefined,
              },
              timeout: 5000,
            }
          );

          const data = response.data || {};
          if (!data.ok || !data.url) {
            throw new Error(data.error || '카메라 직접 연결 URL 생성 실패');
          }

          return {
            entityId,
            url: data.url,
            provider: data.provider || 'homeassistant',
          };
        })
      );

      if (cancelled) return;

      const nextUrls = {};
      const nextProviders = {};
      const nextErrors = {};

      results.forEach((result, index) => {
        const entityId = cameraEntityIds[index];
        if (!entityId) return;

        if (result.status === 'fulfilled') {
          nextUrls[entityId] = result.value.url;
          nextProviders[entityId] = result.value.provider;
        } else {
          nextErrors[entityId] =
            result.reason?.response?.data?.error ||
            result.reason?.message ||
            '카메라 직접 연결 실패';
        }
      });

      setCameraStreamUrls(() => {
        const merged = {};
        cameraEntityIds.forEach((entityId) => {
          merged[entityId] = nextUrls[entityId] || '';
        });
        return merged;
      });
      setCameraStreamProviders(nextProviders);
      setCameraStreamErrors(nextErrors);
    };

    setCameraImageReady({});
    loadDirectCameraUrls();

    return () => {
      cancelled = true;
    };
  }, [
    api,
    cameraEntityIdsKey,
    cameraRefreshKey,
    selectedSiteId,
  ]);


  // 새 URL로 영상 연결을 시작했는데 3초 동안 첫 프레임 이벤트가 없으면
  // 사용자가 누르는 영상 새로고침과 같은 경로로 재시도한다.
  useEffect(() => {
    cameraWatchdogTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    cameraWatchdogTimersRef.current.clear();
    if (document.visibilityState === 'hidden' || !navigator.onLine) return undefined;

    Object.entries(cameraStreamUrls).forEach(([entityId, url]) => {
      if (!url || cameraStreamProviders[entityId] === 'go2rtc' || cameraImageReady[entityId] || cameraStreamErrors[entityId]) return;
      const timer = window.setTimeout(() => {
        cameraWatchdogTimersRef.current.delete(entityId);
        if (document.visibilityState !== 'visible' || !navigator.onLine) return;
        onCameraImageError(entityId);
      }, 3000);
      cameraWatchdogTimersRef.current.set(entityId, timer);
    });
    return () => {
      cameraWatchdogTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      cameraWatchdogTimersRef.current.clear();
    };
  }, [cameraStreamUrls, cameraStreamProviders, cameraImageReady, cameraStreamErrors, onCameraImageError]);


  const renderEntityCard = (entity, mobileTwoColumns = false) => {
    const on = isOnState(entity.state);

    return (
      <div
        key={entity.entity_id}
        className={`${mobileTwoColumns ? 'col-6' : 'col-12'} col-md-6 col-xl-4`}
      >
        <div
          className={`card h-100 border-0 shadow-sm ${
            !entity.available ? 'opacity-75' : ''
          }`}
        >
          <div className="card-body d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div className="d-flex align-items-center gap-3 min-w-0">
                <div
                  className="rounded-3 bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 48,
                    height: 48,
                    fontSize: 24,
                  }}
                >
                  {getEntityIcon(entity)}
                </div>

                <div className="min-w-0">
                  <div className="fw-bold text-break">
                    {getDisplayName(entity)}
                  </div>
                </div>
              </div>

              <span
                className={`badge rounded-pill ${
                  !entity.available
                    ? 'text-bg-danger'
                    : on
                      ? 'text-bg-success'
                      : 'text-bg-secondary'
                }`}
              >
                {!entity.available
                  ? '연결 안 됨'
                  : getStateText(entity)}
              </span>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-end gap-2">
                <span
                  className="fw-bold"
                  style={{ fontSize: '2rem' }}
                >
                  {getStateText(entity)}
                </span>

                {entity.unit_of_measurement && (
                  <span className="text-secondary pb-1">
                    {entity.unit_of_measurement}
                  </span>
                )}
              </div>

              {entity.attributes?.current_temperature !==
                undefined && (
                <div className="small text-secondary mt-1">
                  현재 온도:{' '}
                  {formatSensorValue(
                    entity.attributes.current_temperature
                  )}
                  ℃
                </div>
              )}

              {entity.attributes?.current_position !==
                undefined && (
                <div className="small text-secondary mt-1">
                  현재 위치:{' '}
                  {entity.attributes.current_position}%
                </div>
              )}
            </div>

            <div className="mt-auto">
              {renderEntityControl(entity)}
            </div>
          </div>

          <div className="card-footer bg-white border-0 pt-0 pb-3">
            <div className="small text-secondary">
              최근 갱신: {formatDateTime(entity.last_updated)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const cameraStripRef = useRef(null);
  const scrollCameraStrip = (direction) => {
    const strip = cameraStripRef.current;
    if (!strip) return;
    const card = strip.firstElementChild;
    const distance = card ? card.getBoundingClientRect().width + 16 : strip.clientWidth;
    strip.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  const renderCameraGroup = (items) => {
    if (!items.length) return null;

    const getCameraUrl = (camera) =>
      cameraStreamUrls[camera.entity_id] || '';

    return (
      <>
        <section className="mb-3">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h3 className="h5 fw-bold mb-1">스마트 팜</h3>
              <p className="small text-secondary mb-0">
                농장 상황를 영상으로 확인하고 제어합니다.
              </p>
            </div>
            {items.length > 1 && (
              <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0">
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => scrollCameraStrip(-1)} aria-label="이전 카메라">◀ 이전</button>
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => scrollCameraStrip(1)} aria-label="다음 카메라">다음 ▶</button>
              </div>
            )}
          </div>
          <div
            ref={cameraStripRef}
            className="d-flex gap-3 overflow-x-auto pb-2"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              overscrollBehaviorX: 'contain',
            }}
          >
            {items.map((camera) => {
              const cameraUrl = getCameraUrl(camera);

              return (
                <div
                  key={camera.entity_id}
                  className="flex-shrink-0"
                  style={{
                    width: items.length > 1 ? '92%' : '100%',
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                  }}
                >
                  <div className="card border-0 shadow-sm overflow-hidden">
                    <div
                      className="position-relative bg-dark"
                      style={{ aspectRatio: '16 / 9' }}
                    >
                      {!cameraFullscreenOpen && cameraUrl && (
                        cameraStreamProviders[camera.entity_id] === 'go2rtc' ? (
                          <iframe
                            key={`${camera.entity_id}:${cameraRefreshKey}`}
                            src={cameraUrl}
                            title={getDisplayName(camera)}
                            onLoad={() => markCameraImageReady(camera.entity_id)}
                            onError={() => onCameraImageError(camera.entity_id)}
                            allow="autoplay; fullscreen"
                            className="position-absolute top-0 start-0 w-100 h-100 border-0"
                            style={{ backgroundColor: '#000' }}
                          />
                        ) : (
                          <img
                            key={`${camera.entity_id}:${cameraRefreshKey}`}
                            src={cameraUrl}
                            onError={() => onCameraImageError(camera.entity_id)}
                            onLoad={() => markCameraImageReady(camera.entity_id)}
                            alt={getDisplayName(camera)}
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{ objectFit: 'contain' }}
                          />
                        )
                      )}

                      {!cameraFullscreenOpen && camera.available && !cameraImageReady[camera.entity_id] && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white">
                          {cameraStreamErrors[camera.entity_id] || '카메라 연결 중...'}
                        </div>
                      )}

                      {!camera.available && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white">
                          카메라 연결 안 됨
                        </div>
                      )}
                    </div>

                    <div className="card-body d-flex align-items-center justify-content-between gap-2 flex-wrap">
                      <div className="fw-bold text-truncate flex-grow-1" style={{ minWidth: 0 }}>
                        {getDisplayName(camera)}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {renderCameraPtz(camera)}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                          onClick={() => setCameraFullscreenOpen(true)}
                          aria-label="카메라 전체보기"
                          title="전체보기"
                        >
                          <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>⛶</span>
                          <span className="d-none d-sm-inline">전체보기</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={reconnectCamera}
                        >
                          영상 새로고침
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </section>

        {cameraFullscreenOpen && (
          <div
            className="position-fixed bg-black"
            style={{
              zIndex: 2000,
              inset: 0,
              width: '100vw',
              height: '100dvh',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="카메라 전체보기"
          >
            <div
              className="position-fixed top-0 end-0 d-flex gap-2 p-2"
              style={{ zIndex: 2001 }}
            >
              <button
                type="button"
                className="btn btn-dark btn-sm bg-opacity-75"
                onClick={reconnectCamera}
                aria-label="영상 새로고침"
              >
                ↻
              </button>

              <button
                type="button"
                className="btn btn-dark btn-sm bg-opacity-75"
                onClick={() => {
                  setCameraFullscreenOpen(false);
                }}
                aria-label="전체보기 닫기"
              >
                ✕
              </button>
            </div>

            <div className="d-flex h-100">
              {items.map((camera) => (
                <div
                  key={`fullscreen-${camera.entity_id}`}
                  className="position-relative flex-shrink-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                  }}
                >
                  {getCameraUrl(camera) ? (
                    cameraStreamProviders[camera.entity_id] === 'go2rtc' ? (
                      <iframe
                        key={`fullscreen:${camera.entity_id}:${cameraRefreshKey}`}
                        src={getCameraUrl(camera)}
                        title={getDisplayName(camera)}
                        onLoad={() => markCameraImageReady(camera.entity_id)}
                        onError={() => onCameraImageError(camera.entity_id)}
                        allow="autoplay; fullscreen"
                        className="smartfarm-fullscreen-camera border-0"
                        style={{ width: '100%', height: '100%', backgroundColor: '#000' }}
                      />
                    ) : (
                      <img
                        key={`fullscreen:${camera.entity_id}:${cameraRefreshKey}`}
                        src={getCameraUrl(camera)}
                        onError={() => onCameraImageError(camera.entity_id)}
                        onLoad={() => markCameraImageReady(camera.entity_id)}
                        alt={getDisplayName(camera)}
                        className="smartfarm-fullscreen-camera"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          backgroundColor: '#000',
                        }}
                      />
                    )
                  ) : (
                    <div className="text-white">
                      {cameraStreamErrors[camera.entity_id] || '카메라 연결 중...'}
                    </div>
                  )}

                  {camera.available && !cameraImageReady[camera.entity_id] && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white" style={{ zIndex: 1 }}>
                      {cameraStreamErrors[camera.entity_id] || '카메라 연결 중...'}
                    </div>
                  )}

                  <div
                    className="position-absolute bottom-0 start-0 m-3 px-3 py-2 rounded bg-dark bg-opacity-75 text-white fw-semibold"
                    style={{ zIndex: 2 }}
                  >
                    {getDisplayName(camera)}
                  </div>

                  <div className="position-absolute bottom-0 end-0 m-2 rounded bg-light bg-opacity-75 p-2" style={{ zIndex: 3 }}>
                    {renderCameraPtz(camera)}
                  </div>
                  {!camera.available && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white">
                      카메라 연결 안 됨
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  // 양액 자동화: 자동관수와 동일한 카드 / 설정 모달 UI. 기존 API 로직은 유지한다.
  const renderNutrient = () => {
    const collapsed = id => collapsedNutrientIds === null || collapsedNutrientIds.has(id);
    const toggle = id => setCollapsedNutrientIds(prev => {
      const next = new Set(prev || nutrients.map(item => item.id));
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    const settingLabels = [
      ['목표 EC (설정값)', 'target_ec'], ['목표 pH', 'target_ph'],
      ['1회 투입 시간 (초)', 'dose_seconds'], ['혼합 시간 (초)', 'mix_seconds'],
      ['안정화 대기 (초)', 'settle_seconds'], ['제어 주기 (초)', 'cycle_interval'],
    ];
    const outputLabels = { a:'A액', b:'B액', c:'pH 조정', mix:'혼합', water:'원수', supply:'공급', aux:'예비' };
    const showModal = nutrientOpen || nutrientEditId !== null;
    if (nutrients.length === 0 && !showModal) return null;
    return (
      <section className="mb-3" aria-label="양액 자동화">
        <div className="p-3 p-md-4" style={{border:'1px solid #dee2e6',borderRadius:18,background:'#fff',overflow:'hidden'}}>
          {nutrients.map((item,index) => {
            const isCollapsed = collapsed(item.id);
            return (
              <div key={item.id} className={index > 0 ? 'pt-4 mt-4 border-top' : ''}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <button type="button" className="border-0 bg-transparent p-0 text-start min-w-0 flex-grow-1 d-flex align-items-center"
                    onClick={() => toggle(item.id)} aria-expanded={!isCollapsed} aria-label={`${item.name || '양액 자동화'} ${isCollapsed ? '펼치기' : '접기'}`}>
                    <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                      <span className="fw-semibold fs-7">{item.name || '양액 자동화'}</span>
                      <span className={`fw-semibold ${item.active ? 'text-success' : 'text-secondary'}`} style={{whiteSpace:'nowrap'}}>{item.status_verified !== true ? '상태 확인 불가' : item.active === true ? '실행 중' : '중지'}</span>
                      <span className="text-secondary d-inline-flex align-items-center" aria-hidden="true"
                        style={{transform:isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',transition:'transform 0.18s ease'}}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                      </span>
                    </div>
                  </button>
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <button type="button" className="btn btn-sm border-0 bg-transparent p-2 d-flex align-items-center justify-content-center"
                      disabled={nutrientBusy || item.active !== false || item.status_verified !== true} title="설정" aria-label="양액 자동화 설정" style={{color:'#6c757d'}} onClick={() => editNutrient(item)}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2.4a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.12.6.65 1.03 1.27 1.03H21a2 2 0 1 1 0 4h-.33c-.62 0-1.15.43-1.27.97Z"/></svg>
                    </button>
                    <button type="button" className={`btn btn-sm fw-semibold ${item.active ? 'btn-outline-secondary' : 'btn-primary'}`}
                      style={{whiteSpace:'nowrap'}} disabled={nutrientBusy || item.status_verified !== true} aria-pressed={item.active === true}
                      onClick={() => controlNutrient(item,item.active !== true)}>{nutrientBusy ? '처리 중…' : item.status_verified !== true ? '상태 확인 불가' : item.active === true ? '중지' : '실행'}</button>
                  </div>
                </div>
                {!isCollapsed && <div className="mt-3">
                  <div className="small text-secondary mb-3">목표 EC {item.target_ec} · 목표 pH {item.target_ph} · {item.dose_seconds}초 투입 · {item.cycle_interval}초 주기</div>
                  <div className="mt-3 mb-3">
                    <div className="row g-2">
                      {[['a','A액'],['b','B액'],['c','pH 조절'],['mix','혼합'],['water','원수'],['supply','공급']].map(([role,label]) => {
                        const relay = nutrientRelayStates[item.id];
                        const state = relay?.states?.[role] || 'unknown';
                        const busy = nutrientRelayBusy === `${item.id}:${role}`;
                        const allowed = relay?.ok === true && relay.active === false && !busy;
                        return <div key={role} className="col-6 col-md-4">
                          <div className="border rounded-3 p-2 h-100">
                            <div className="small fw-semibold">{label} <span className={state === 'on' ? 'text-success' : 'text-secondary'}>{state === 'on' ? 'ON' : state === 'off' ? 'OFF' : '확인 불가'}</span></div>
                            <div className="d-flex gap-1 mt-2">
                              <button type="button" className="btn btn-sm btn-outline-success flex-fill" disabled={!allowed} onClick={() => controlNutrientRelay(item,role,'on')}>ON</button>
                              <button type="button" className="btn btn-sm btn-outline-secondary flex-fill" disabled={!allowed} onClick={() => controlNutrientRelay(item,role,'off')}>OFF</button>
                            </div>
                          </div>
                        </div>;
                      })}
                    </div>
                  </div>
                  <div className="row g-2">
                    {settingLabels.map(([label,key]) => <div className="col-6 col-md-4" key={key}>
                      <div className="bg-light rounded-3 p-2 h-100"><div className="small text-secondary">{label}</div><div className="fw-semibold mt-1">{item[key] ?? '-'}</div></div>
                    </div>)}
                  </div>
                </div>}
              </div>
            );
          })}
        </div>
        {showModal && <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{zIndex:2000,background:'rgba(0,0,0,0.45)'}} role="dialog" aria-modal="true" aria-label={nutrientEditId !== null ? '양액 자동화 설정' : '양액 자동화 추가'}
          onMouseDown={e => {if(e.target===e.currentTarget && !nutrientBusy){setNutrientOpen(false);setNutrientEditId(null);}}}>
          <div className="bg-white rounded-4 shadow-lg w-100" style={{maxWidth:500,maxHeight:'92vh',overflowY:'auto'}}>
            <div className="d-flex align-items-center justify-content-between px-4 pt-4 pb-3 border-bottom">
              <div><div className="fw-bold fs-5">{nutrientEditId !== null ? '양액 자동화 설정' : '양액 자동화 추가'}</div>
                <div className="small text-secondary mt-1">{nutrientEditId !== null ? '목표값과 운전 조건을 수정합니다.' : 'EC·pH 센서와 릴레이를 연결합니다.'}</div></div>
              <button type="button" className="btn-close" aria-label="닫기" disabled={nutrientBusy}
                onClick={() => {setNutrientOpen(false);setNutrientEditId(null);}} />
            </div>
            <div className="p-4"><div className="row g-3">
              {nutrientEditId === null && <>
                <div className="col-12"><label className="form-label small fw-semibold">양액기 이름</label>
                  <input className="form-control" value={nutrientForm.name} onChange={e=>nutrientUpdate('name',e.target.value)}/></div>
                {([['EC 센서','ec'],['pH 센서','ph']]).map(([label,key]) => <div className="col-12" key={key}>
                  <label className="form-label small fw-semibold">{label} (자동 Input 등록)</label>
                  <select className="form-select" value={nutrientAuto.sensors[key]} onChange={e=>setNutrientAuto(v=>({...v,sensors:{...v.sensors,[key]:e.target.value}}))}>
                    <option value="">센서 선택</option>
                    {nutrientDevices.sensors.filter(x=>x.kind===key).map(x=><option key={`${x.device_id}::${x.entity_id}`} value={`${x.device_id}::${x.entity_id}`}>{x.device_name} / {x.entity_name}</option>)}
                  </select>
                </div>)}
                {Object.entries(outputLabels).map(([key,label]) => <div className="col-12 col-md-6" key={key}>
                  <label className="form-label small fw-semibold">{label} 릴레이 (자동 Output 등록)</label>
                  <select className="form-select" value={nutrientAuto.outputs[key]} onChange={e=>setNutrientAuto(v=>({...v,outputs:{...v.outputs,[key]:e.target.value}}))}>
                    <option value="">장치 선택</option>
                    {nutrientDevices.outputs.map(x=><option key={`${x.device_id}::${x.entity_id}`} value={`${x.device_id}::${x.entity_id}`}>{x.device_name} / {x.entity_name}</option>)}
                  </select>
                </div>)}
              </>}
              {settingLabels.map(([label,key]) => <div className="col-12 col-md-6" key={key}>
                <label className="form-label small fw-semibold">{label}</label>
                <input type="number" step="any" className="form-control" value={nutrientEditId !== null ? nutrientEditForm[key] ?? '' : nutrientForm[key]}
                  onChange={e=>nutrientEditId !== null ? setNutrientEditForm(v=>({...v,[key]:e.target.value})) : nutrientUpdate(key,e.target.value)}/>
              </div>)}
              {nutrientEditId === null && <div className="col-12"><div className="small text-secondary bg-light rounded-3 p-3">선택한 ESP32 장치로 Mycodo Input·Output·Function을 자동 등록합니다. 신규 자동화는 중지 상태로 생성됩니다.</div></div>}
            </div></div>
            <div className="d-flex gap-2 align-items-center px-4 pb-4">
              {nutrientEditId !== null && (
                <button type="button" className="btn btn-outline-danger fw-semibold me-auto"
                  disabled={nutrientBusy}
                  onClick={() => {
                    const target = nutrients.find(item => String(item.id) === String(nutrientEditId));
                    if (target) deleteNutrient(target);
                  }}>
                  삭제
                </button>
              )}
              <button type="button" className="btn btn-outline-secondary" disabled={nutrientBusy} onClick={()=>{setNutrientOpen(false);setNutrientEditId(null);}}>취소</button>
              <button type="button" className="btn btn-primary" onClick={nutrientEditId !== null ? updateNutrient : saveNutrient}
                disabled={nutrientBusy || (nutrientEditId === null && (!nutrientAuto.sensors.ec || !nutrientAuto.sensors.ph || ['a','b','c','mix','water','supply'].some(k=>!nutrientAuto.outputs[k])))}>
                {nutrientBusy ? '저장 중…' : nutrientEditId !== null ? '설정 저장' : '양액 자동화 생성'}
              </button>
            </div>
          </div>
        </div>}
      </section>
    );
  };

  const renderAutomationStatus = (hasCamera = false) => {
    if (!multiAutomationLoading && multiAutomations.length === 0) return null;

    return (
      <section
        className={hasCamera ? 'mt-1 mb-3' : 'mb-3'}
        aria-label="Mycodo 자동관수"
      >
        <div className="d-flex justify-content-between align-items-center gap-2 px-1">
        </div>

        <div
          className="p-3 p-md-4"
          style={{
            border: '1px solid #dee2e6',
            borderRadius: 18,
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          {multiAutomationLoading && multiAutomations.length === 0 ? (
            <div className="bg-light rounded-3 p-3 text-center text-secondary small">
              자동관수 정보를 불러오고 있습니다.
            </div>
          ) : multiAutomations.length === 0 ? (
            <div className="bg-light rounded-3 p-3 text-center text-secondary small">
              등록된 자동관수가 없습니다. 필요한 센서와 릴레이를 선택해 추가하세요.
            </div>
          ) : (
            <div>
              {multiAutomations.map((automation, index) => {
                const busy = multiAutomationActionId === automation.id;
                const pumpBusy = multiPumpActionId === automation.id;
                const pumpOn = String(automation.pump_state || '').toLowerCase() === 'on';
                const collapsed = collapsedAutomationIds === null || collapsedAutomationIds.has(automation.id);

                return (
                  <div
                    key={automation.id}
                    className={index > 0 ? 'pt-4 mt-4 border-top' : ''}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-start min-w-0 flex-grow-1 d-flex align-items-center"
                        onClick={() => toggleAutomationCollapsed(automation.id)}
                        aria-expanded={!collapsed}
                        aria-label={`${automation.name || '자동관수'} ${collapsed ? '펼치기' : '접기'}`}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                          <span className="fw-semibold">{automation.name || '자동관수'}</span>
                          <span
                            className={`fw-semibold ${automation.active ? 'text-success' : 'text-secondary'}`}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {automation.active ? '실행 중' : '중지'}
                          </span>
                          <span
                            className="text-secondary d-inline-flex align-items-center"
                            aria-hidden="true"
                            style={{ transition: 'transform 0.18s ease', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </span>
                        </div>
                      </button>

                      <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                        <button
                          type="button"
                          className="btn btn-sm border-0 bg-transparent p-2 d-flex align-items-center justify-content-center"
                          onClick={() => openEditAutomation(automation)}
                          disabled={busy || multiAutomationSaving}
                          aria-label="자동관수 설정"
                          title="설정"
                          style={{ color: '#6c757d' }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2.4a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.12.6.65 1.03 1.27 1.03H21a2 2 0 1 1 0 4h-.33c-.62 0-1.15.43-1.27.97Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm fw-semibold ${automation.active ? 'btn-outline-secondary' : 'btn-primary'}`}
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={() => controlMultiAutomation(automation)}
                          disabled={busy || multiAutomationSaving}
                          aria-pressed={automation.active}
                        >
                          {automation.active ? '중지' : '실행'}
                        </button>
                      </div>
                    </div>

                    {!collapsed && (
                      <>
                        <div className="small text-secondary mt-2">
                          수분 {automation.soil_threshold}% 이하 · {automation.watering_seconds}초 관수 · {automation.watering_interval}초 대기
                        </div>

                        <div className="row g-2 mt-2">
                      <div className="col-6">
                        <div className="bg-light rounded-3 h-100 p-3 text-center">
                          <div className="small text-secondary text-truncate">
                            {automation.soil_entity_name || '토양 수분'}
                          </div>
                          <div className="fw-bold mt-2" style={{ fontSize: '1.45rem' }}>
                            {automation.soil_value !== null && automation.soil_value !== undefined
                              ? `${formatSensorValue(automation.soil_value)} %`
                              : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <button
                          type="button"
                          className="border-0 bg-light rounded-3 h-100 w-100 p-3 text-center d-flex flex-column align-items-center justify-content-center"
                          onClick={() => controlMultiPump(automation, pumpOn ? 'off' : 'on')}
                          disabled={pumpBusy || multiAutomationSaving}
                          aria-pressed={pumpOn}
                          aria-label={`${automation.pump_entity_name || '물펌프'} ${pumpOn ? '끄기' : '켜기'}`}
                          style={{ minHeight: '100%', cursor: pumpBusy ? 'wait' : 'pointer' }}
                        >
                          <div className="small text-secondary text-truncate w-100">
                            {automation.pump_entity_name || '물펌프'}
                          </div>
                          <div className="fw-bold mt-2" style={{ fontSize: '1.2rem' }}>
                            {automation.pump_state ? (pumpOn ? '켜짐' : '꺼짐') : '-'}
                          </div>
                        </button>
                      </div>
                        </div>
                      </>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderAutomationSettingsModal = () => {
    if (!automationSettingsOpen) return null;

    const isEdit = Boolean(editingAutomationId);
    const editingAutomation = multiAutomations.find(
      (item) => Number(item.id) === Number(editingAutomationId)
    );
    const availableSoils = automationEntities.soils.filter((item) => !item.used);
    const availablePumps = automationEntities.pumps.filter((item) => !item.used);

    const updateForm = (key, value) => {
      setMultiAutomationForm((prev) => ({ ...prev, [key]: value }));
    };

    const chooseSoil = (value) => {
      const item = automationEntities.soils.find(
        (candidate) => `${candidate.device_id}::${candidate.entity_id}` === value
      );
      setMultiAutomationForm((prev) => ({
        ...prev,
        soil_device_id: item?.device_id || '',
        soil_entity_id: item?.entity_id || '',
      }));
    };

    const choosePump = (value) => {
      const item = automationEntities.pumps.find(
        (candidate) => `${candidate.device_id}::${candidate.entity_id}` === value
      );
      setMultiAutomationForm((prev) => ({
        ...prev,
        pump_device_id: item?.device_id || '',
        pump_entity_id: item?.entity_id || '',
      }));
    };

    const selectedSoilValue = multiAutomationForm.soil_entity_id
      ? `${multiAutomationForm.soil_device_id}::${multiAutomationForm.soil_entity_id}`
      : '';
    const selectedPumpValue = multiAutomationForm.pump_entity_id
      ? `${multiAutomationForm.pump_device_id}::${multiAutomationForm.pump_entity_id}`
      : '';

    return (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
        style={{ zIndex: 2000, background: 'rgba(0,0,0,0.45)' }}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? '자동관수 설정 수정' : '자동관수 추가'}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !multiAutomationSaving) {
            setAutomationSettingsOpen(false);
          }
        }}
      >
        <div className="bg-white rounded-4 shadow-lg w-100" style={{ maxWidth: 500, maxHeight: '92vh', overflowY: 'auto' }}>
          <div className="d-flex align-items-center justify-content-between px-4 pt-4 pb-3 border-bottom">
            <div>
              <div className="fw-bold fs-5">{isEdit ? '자동관수 설정' : '자동관수 추가'}</div>
              <div className="small text-secondary mt-1">
                {isEdit ? '관수 조건을 수정합니다.' : '토양수분 센서와 관수 릴레이를 연결합니다.'}
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="닫기"
              disabled={multiAutomationSaving}
              onClick={() => setAutomationSettingsOpen(false)}
            />
          </div>

          <div className="p-4">
            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="automationName">이름</label>
              <input
                id="automationName"
                type="text"
                maxLength="120"
                className="form-control"
                value={multiAutomationForm.name}
                onChange={(e) => updateForm('name', e.target.value)}
                disabled={multiAutomationSaving}
                placeholder="예: 1구역 자동관수"
              />
            </div>

            {!isEdit ? (
              <>
                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="automationSoil">토양수분 센서</label>
                  <select
                    id="automationSoil"
                    className="form-select"
                    value={selectedSoilValue}
                    onChange={(e) => chooseSoil(e.target.value)}
                    disabled={multiAutomationSaving}
                  >
                    <option value="">센서를 선택하세요</option>
                    {availableSoils.map((item) => (
                      <option key={`${item.device_id}-${item.entity_id}`} value={`${item.device_id}::${item.entity_id}`}>
                        {item.device_name || `ESP32 #${item.device_id}`} · {item.entity_name || item.entity_id}
                      </option>
                    ))}
                  </select>
                  {availableSoils.length === 0 && (
                    <div className="form-text text-danger">사용 가능한 토양수분 센서가 없습니다.</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" htmlFor="automationPump">관수 릴레이</label>
                  <select
                    id="automationPump"
                    className="form-select"
                    value={selectedPumpValue}
                    onChange={(e) => choosePump(e.target.value)}
                    disabled={multiAutomationSaving}
                  >
                    <option value="">릴레이를 선택하세요</option>
                    {availablePumps.map((item) => (
                      <option key={`${item.device_id}-${item.entity_id}`} value={`${item.device_id}::${item.entity_id}`}>
                        {item.device_name || `ESP32 #${item.device_id}`} · {item.entity_name || item.entity_id}
                      </option>
                    ))}
                  </select>
                  {availablePumps.length === 0 && (
                    <div className="form-text text-danger">사용 가능한 관수 릴레이가 없습니다.</div>
                  )}
                </div>
              </>
            ) : null}

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="multiSoilThreshold">토양수분 기준</label>
              <div className="input-group">
                <input
                  id="multiSoilThreshold"
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  className="form-control"
                  value={multiAutomationForm.soil_threshold}
                  onChange={(e) => updateForm('soil_threshold', e.target.value)}
                  disabled={multiAutomationSaving}
                />
                <span className="input-group-text">%</span>
              </div>
              <div className="form-text">이 값 이하일 때 관수를 시작합니다.</div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="multiWateringSeconds">1회 관수시간</label>
              <div className="input-group">
                <input
                  id="multiWateringSeconds"
                  type="number"
                  min="1"
                  max="3600"
                  step="1"
                  className="form-control"
                  value={multiAutomationForm.watering_seconds}
                  onChange={(e) => updateForm('watering_seconds', e.target.value)}
                  disabled={multiAutomationSaving}
                />
                <span className="input-group-text">초</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold" htmlFor="multiWateringInterval">재관수 대기시간</label>
              <div className="input-group">
                <input
                  id="multiWateringInterval"
                  type="number"
                  min="10"
                  max="86400"
                  step="1"
                  className="form-control"
                  value={multiAutomationForm.watering_interval}
                  onChange={(e) => updateForm('watering_interval', e.target.value)}
                  disabled={multiAutomationSaving}
                />
                <span className="input-group-text">초</span>
              </div>
            </div>

            <div className="d-flex gap-2 align-items-center">
              {isEdit && (
                <button
                  type="button"
                  className="btn btn-outline-danger fw-semibold me-auto"
                  disabled={multiAutomationSaving || Boolean(multiAutomationActionId)}
                  onClick={() => deleteMultiAutomation(editingAutomation)}
                >
                  삭제
                </button>
              )}
              <button
                type="button"
                className="btn btn-light"
                disabled={multiAutomationSaving || Boolean(multiAutomationActionId)}
                onClick={() => setAutomationSettingsOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary fw-semibold"
                disabled={
                  multiAutomationSaving ||
                  Boolean(multiAutomationActionId) ||
                  (!isEdit && (availableSoils.length === 0 || availablePumps.length === 0))
                }
                onClick={saveMultiAutomation}
              >
                {multiAutomationSaving ? '저장 중…' : isEdit ? '설정 저장' : '자동관수 생성'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileEnvironmentCard = (entity) => (
    <div key={`mobile-${entity.entity_id}`} className="col-4">
      <div
        className={`card h-100 border-0 shadow-sm text-center ${
          !entity.available ? 'opacity-75' : ''
        }`}
      >
        <div className="card-body px-1 py-3 d-flex flex-column align-items-center justify-content-center">
          <div
            className="rounded-circle bg-light d-flex align-items-center justify-content-center mb-2"
            style={{ width: 42, height: 42, fontSize: 21 }}
          >
            {getEntityIcon(entity)}
          </div>

          <div
            className="fw-semibold text-truncate w-100 mb-1"
            style={{ fontSize: '0.78rem' }}
            title={getDisplayName(entity)}
          >
            {getDisplayName(entity)}
          </div>

          <div
            className="fw-bold text-nowrap"
            style={{ fontSize: '1.05rem', lineHeight: 1.2 }}
          >
            {getStateText(entity)}
            {entity.unit_of_measurement && (
              <span className="ms-1 text-secondary fw-normal" style={{ fontSize: '0.72rem' }}>
                {entity.unit_of_measurement}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // MQTT 장치 목록은 농장 선택 시 한 번만 조회한다.
  // 이후 실제 릴레이 상태와 연결 여부는 장치별 WebSocket 이벤트로만 갱신한다.
  useEffect(() => {
    if (!selectedSiteId) {
      setMqttSwitches([]);
      setMqttError('');
      return undefined;
    }

    let disposed = false;
    const siteId = Number(selectedSiteId);
    const sockets = new Map();
    const reconnectTimers = new Map();
    const deviceIds = new Set();
    setMqttSwitches([]);
    setMqttError('');

    const updateDevice = (deviceId, entities, online) => {
      if (disposed) return;
      const switches = (Array.isArray(entities) ? entities : [])
        .filter((entity) => entity.domain === 'switch' && entity.command_topic)
        .map((entity) => ({ ...entity, device_id: deviceId, online: Boolean(online) }));
      setMqttSwitches((previous) => {
        const next = [...previous];
        switches.forEach((entity) => {
          const index = next.findIndex((item) =>
            Number(item.device_id) === deviceId &&
            String(item.entity_id) === String(entity.entity_id));
          const old = index >= 0 ? next[index] : null;
          const updated = {
            ...entity,
            display_name: entity.display_name || old?.display_name || entity.name,
          };
          if (index >= 0) next[index] = updated;
          else next.push(updated);
        });
        // State updates never remove or move existing relay cards.
        return next;
      });
    };

    const connect = (deviceId) => {
      if (disposed || !deviceIds.has(deviceId)) return;
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${scheme}://${window.location.host}/api/member/smartfarm/ws` +
        `?site_id=${encodeURIComponent(siteId)}&device_id=${encodeURIComponent(deviceId)}`;
      const socket = new WebSocket(url);
      sockets.set(deviceId, socket);

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data || '{}');
          if (data.type !== 'state' || Number(data.site_id) !== siteId ||
              Number(data.device_id) !== deviceId) return;
          updateDevice(deviceId, data.entities, data.online);
        } catch (error) {
          console.warn('MQTT 상태 메시지 처리 실패:', error);
        }
      };
      socket.onclose = () => {
        if (sockets.get(deviceId) === socket) sockets.delete(deviceId);
        if (disposed || !deviceIds.has(deviceId)) return;
        // 연결이 끊기면 제어를 막되 마지막으로 확인된 상태는 보존한다.
        setMqttSwitches((previous) => previous.map((entity) =>
          Number(entity.device_id) === deviceId ? { ...entity, online: false } : entity));
        reconnectTimers.set(deviceId, window.setTimeout(() => {
          reconnectTimers.delete(deviceId);
          connect(deviceId);
        }, 2000));
      };
    };

    const initialize = async () => {
      try {
        const response = await api.get('/api/member/smartfarm/devices', {
          params: { site_id: siteId },
        });
        if (response.data?.ok === false) throw new Error(response.data.error || 'MQTT 장치 조회 실패');
        if (disposed) return;
        const devices = Array.isArray(response.data?.devices) ? response.data.devices : [];
        devices.forEach((device) => {
          const deviceId = Number(device.device_id);
          if (!Number.isInteger(deviceId) || deviceId <= 0) return;
          deviceIds.add(deviceId);
          updateDevice(deviceId, device.entities, device.online);
          connect(deviceId);
        });
      } catch (error) {
        if (!disposed) setMqttError(error.response?.data?.error || error.message || 'MQTT 장치 조회 실패');
      }
    };
    initialize();

    return () => {
      disposed = true;
      deviceIds.clear();
      reconnectTimers.forEach((timer) => window.clearTimeout(timer));
      sockets.forEach((socket) => {
        socket.onclose = null;
        socket.onmessage = null;
        socket.close();
      });
    };
  }, [api, selectedSiteId]);

  const controlMqttSwitch = async (entity) => {
    if (!selectedSiteId || mqttBusyKey || !entity.online) return;
    const key = `${entity.device_id}:${entity.entity_id}`;
    const current = String(entity.value ?? '').trim().toLowerCase();
    const state = ['on', '1', 'true'].includes(current) ? 'off' : 'on';
    setMqttBusyKey(key);
    setMqttError('');
    try {
      const result = await api.post('/api/member/smartfarm/entity-command', {
        site_id: Number(selectedSiteId),
        device_id: entity.device_id,
        entity_id: entity.entity_id,
        state,
      });
      if (result.data?.ok === false) throw new Error(result.data.error || '명령 전송 실패');
      // 실제 ON/OFF 표시는 다음 MQTT 상태 수신 결과로만 갱신한다.
    } catch (error) {
      setMqttError(error.response?.data?.error || error.message || '릴레이 제어에 실패했습니다.');
    } finally {
      setMqttBusyKey('');
    }
  };

  const renderMqttSwitchPanel = () => (
    <section className="mb-3" aria-label="MQTT 릴레이 수동 제어">
      {mqttSwitches.length > 0 && (
        <>
          <div className="row g-2">
            {[...mqttSwitches].sort((a, b) =>
              Number(a.device_id) - Number(b.device_id) ||
              String(a.entity_id).localeCompare(String(b.entity_id))
            ).map((entity) => {
              const key = `${entity.device_id}:${entity.entity_id}`;
              const value = String(entity.value ?? '').trim().toLowerCase();
              const on = ['on', '1', 'true'].includes(value);
              return (
                <div className="col-4 col-lg-3" key={key}>
                  <div className="h-100 rounded-3 bg-light p-2 d-flex flex-column text-center">
                    <div className="small text-secondary mb-2" style={{ overflowWrap: 'anywhere' }}>
                      {entity.display_name || entity.name || entity.entity_id}
                    </div>
                    <div className={`fw-semibold mb-2 ${on && entity.online ? 'text-success' : 'text-secondary'}`}>
                      {!entity.online ? '연결 안 됨' : value === '' || value === 'unknown' ? '상태 확인 중' : on ? '켜짐' : '꺼짐'}
                    </div>
                    <button type="button"
                      className={`btn btn-sm mt-auto ${on ? 'btn-outline-danger' : 'btn-primary'}`}
                      style={{ minHeight: 40 }}
                      disabled={Boolean(mqttBusyKey) || !entity.online || !['on', 'off', '1', '0', 'true', 'false'].includes(value)}
                      onClick={() => controlMqttSwitch(entity)}>
                      {mqttBusyKey === key ? '처리 중…' : on ? '끄기' : '켜기'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {mqttError && <div className="small text-danger mt-2" role="alert">{mqttError}</div>}
    </section>
  );

  // CCTV 아래 통합 패널
  // 카메라를 제외한 모든 등록 엔티티(온도/습도/토양수분/스위치/센서/기타 제어)를
  // 같은 회색 카드 영역에 표시한다. 기존 제어 로직은 renderEntityControl()을 그대로 사용한다.
  const panelEntities = entities.filter((entity) => entity.domain !== 'camera');

  const renderQuickPanel = () => (
    <section className="mb-3" aria-label="농장 상태와 즉시 제어">
      <div className="row g-2">
        {panelEntities.map((entity) => {
          const busy = controlLoadingId === entity.entity_id;
          const on = isOnState(entity.state);
          const isBasicPower = ['switch', 'light', 'fan', 'input_boolean'].includes(
            entity.domain
          );

          return (
            <div className="col-4 col-lg-3" key={entity.entity_id}>
              <div className="h-100 rounded-3 bg-light p-2 d-flex flex-column text-center">
                <div
                  className="small text-secondary mb-2"
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {getDisplayName(entity)}
                </div>

                <div
                  className={`fw-semibold mb-2 ${
                    on && entity.available ? 'text-success' : 'text-secondary'
                  }`}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {entity.available ? getStateText(entity) : '연결 안 됨'}
                  {entity.available &&
                    (entity.unit_of_measurement ||
                      entity.attributes?.unit_of_measurement) && (
                      <span className="small fw-normal ms-1">
                        {entity.unit_of_measurement ||
                          entity.attributes?.unit_of_measurement}
                      </span>
                    )}
                </div>

                {isBasicPower ? (
                  <button
                    type="button"
                    className={`btn btn-sm mt-auto ${
                      on ? 'btn-outline-danger' : 'btn-primary'
                    }`}
                    style={{ minHeight: 40 }}
                    onClick={() =>
                      controlEntity(entity, on ? 'turn_off' : 'turn_on')
                    }
                    disabled={
                      Boolean(controlLoadingId) ||
                      !entity.available ||
                      entity.controllable === false
                    }
                  >
                    {busy ? '처리 중…' : on ? '끄기' : '켜기'}
                  </button>
                ) : (
                  <div className="mt-auto">
                    {renderEntityControl(entity)}
                  </div>
                )}
              </div>
            </div>
          );
        })}

      </div>
    </section>
  );

  const renderGroup = (title, description, items) => {
    if (!items.length) return null;

    const isEnvironment = title === '농장 환경';

    return (
      <section className="mb-3">
        <div className="mb-3">
          <h3 className="h5 fw-bold mb-1">{title}</h3>
          <p className="small text-secondary mb-0">
            {description}
          </p>
        </div>

        {isEnvironment && (
          <div className="row g-2 d-md-none">
            {items.map(renderMobileEnvironmentCard)}
          </div>
        )}

        <div className={`row g-3 ${isEnvironment ? 'd-none d-md-flex' : ''}`}>
          {items.map((entity) => renderEntityCard(entity, false))}
        </div>
      </section>
    );
  };

  if (pageLoading) {
    return (
      <div
        className="container-fluid farm-diary-page d-flex align-items-center justify-content-center"
        style={{ maxWidth: 1380, minHeight: 'calc(100vh - 100px)', }}
      >
        <div
          className="spinner-border text-primary"
          role="status"
          aria-label="로딩 중"
        />
      </div>
    );
  }

  const hasSite = sites.length > 0;
  const currentRequest = requestStatus?.request;
  const canRequest =
    !currentRequest ||
    ['CANCEL', 'REJECT', 'COMPLETE'].includes(
      currentRequest.status
    );

  return (
    <div
      className="container-fluid mt-2 py-3 farm-diary-page"
      style={{ maxWidth: 1380 }}
    >
      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setError('')}
          />
        </div>
      )}

      {notice && (
        <div
          className="alert alert-success d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{notice}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setNotice('')}
          />
        </div>
      )}

      {!hasSite ? (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-6">
            <div className="card border-0">
              <div className="card-body p-4 p-md-5">
                <div className="text-center mb-4">
                  <div
                    className="mx-auto mb-3 rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{
                      width: 80,
                      height: 80,
                      fontSize: 40,
                    }}
                  >
                    🌱
                  </div>

                  <h3 className="h4 fw-bold">
                    등록된 스마트팜이 없습니다
                  </h3>

                  <p className="text-secondary mb-0">
                    신청 후 관리자가 농장에 장비와 프로그램을 설치하면 이 화면에서 사용할 수
                    있습니다.
                  </p>
                </div>

                {currentRequest && (
                  <div className="border rounded p-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                      <strong>최근 신청 상태</strong>
                      <span
                        className={`badge rounded-pill ${getStatusBadgeClass(
                          currentRequest.status
                        )}`}
                      >
                        {getStatusText(
                          currentRequest.status
                        )}
                      </span>
                    </div>

                    {currentRequest.memo && (
                      <p className="mb-2 text-secondary">
                        {currentRequest.memo}
                      </p>
                    )}

                    <div className="small text-secondary">
                      신청일:{' '}
                      {formatDateTime(
                        currentRequest.created_at
                      )}
                    </div>

                    {currentRequest.status === 'REQUEST' && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mt-3"
                        onClick={cancelRequest}
                        disabled={requestLoading}
                      >
                        {requestLoading
                          ? '취소 처리 중...'
                          : '신청 취소'}
                      </button>
                    )}
                  </div>
                )}

                {canRequest && (
                  <form onSubmit={submitRequest}>
                    <label
                      htmlFor="requestMemo"
                      className="form-label fw-semibold"
                    >
                      설치 환경 또는 요청사항
                    </label>

                    <textarea
                      id="requestMemo"
                      className="form-control mb-3"
                      rows={5}
                      maxLength={2000}
                      value={requestMemo}
                      onChange={(event) =>
                        setRequestMemo(
                          event.target.value
                        )
                      }
                      placeholder="예: 비닐하우스 1동, 온습도 확인과 관수 펌프 제어가 필요합니다."
                    />

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={requestLoading}
                    >
                      {requestLoading
                        ? '신청 중...'
                        : '스마트팜 신청'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <div className="d-flex flex-row gap-2 align-items-center">
              <select
                id="siteSelect"
                aria-label="농장 선택"
                className="form-select fw-semibold"
                value={selectedSiteId}
                onChange={(event) =>
                  setSelectedSiteId(event.target.value)
                }
              >
                {sites.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.site_name} · 기기 {item.entity_count || 0}개
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="btn btn-primary flex-shrink-0"
                onClick={() =>
                  navigate(`/smartfarm/3d?site_id=${encodeURIComponent(selectedSiteId)}`)
                }
                disabled={!selectedSiteId}
              >
                3D 농장 보기
              </button>
            </div>
          </div>

          {dashboardLoading ? (
            <div className="py-5 text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                aria-label="대시보드 로딩 중"
              />
              <div className="mt-3 text-secondary">
                {site?.site_name || '농장'} 상태를
                불러오고 있습니다.
              </div>
            </div>
          ) : entities.length === 0 && registeredCameras.length === 0 && mqttSwitches.length === 0 ? (
            <div className="card border-0">
              <div className="card-body py-5 text-center">
                <div className="fs-2 mb-3">📟</div>
                <h3 className="h5 fw-bold">
                  표시할 기기가 없습니다
                </h3>
                <p className="text-secondary mb-0">
                  관리자에게 회원 화면에 표시할 기기 등록을
                  요청하세요.
                </p>
              </div>
            </div>
          ) : (
            <>
              {renderCameraGroup(groupedEntities.camera)}
          {renderAutomationStatus(groupedEntities.camera.length > 0)}
              <ClimateAutomationPanel siteId={selectedSiteId} basePath="/api/member/smartfarm" showAddButton={false} />
              {renderNutrient()}
              {renderQuickPanel()}
              {renderMqttSwitchPanel()}
              {renderAutomationSettingsModal()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MemberSmartfarm;